import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { Subscription } from 'rxjs/Subscription';
import { Observable } from 'rxjs/Observable';
import { of } from 'rxjs/observable/of';
import { empty } from 'rxjs/observable/empty';
import { map } from 'rxjs/operators/map';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { startWith } from 'rxjs/operators/startWith';
import { tap } from 'rxjs/operators/tap';

import { endOfDay, getMonth, getYear, isWithinRange, parse } from 'date-fns';

import { SessionStorageService, SessionStorage } from '../jng2-webstorage/app';

import { ConfigService } from '../config/config.service';
import { UserInfo } from '../user/user.service';

@Injectable()
export class ApplicationService {
    @SessionStorage() _AcceleratedPrograms: ProgramInfo[];
    @SessionStorage() _AllCollegeYears: string[];
    @SessionStorage() _AllColleges: CollegeInfo[];
    @SessionStorage() _AllSchools: SchoolInfo[];
    @SessionStorage() _AllSchoolYears: string[];
    @SessionStorage() _AllSchoolYearsForAdoptions: string[];
    @SessionStorage() _CurrentSchoolYear: string;
    @SessionStorage() _CurrentSchool: string;
    @SessionStorage() _DualEnrollmentPrograms: ProgramInfo[];
    @SessionStorage() _SelectedAdminCollegeID: number;
    @SessionStorage() _SelectedSchoolYear: string;
    @SessionStorage() _SelectedStudent: StudentInfo;
    @SessionStorage() ShowSummerRolloverNote: boolean;

    AllCollegesSubject: BehaviorSubject<CollegeInfo[]> = new BehaviorSubject<CollegeInfo[]>(null);
    CurrentSchoolSubject: BehaviorSubject<string> = new BehaviorSubject<string>(null);
    CurrentSchoolYearSubject: BehaviorSubject<string> = new BehaviorSubject<string>('');
    DevSiteSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    ParentStudentChildrenSubject: BehaviorSubject<BasicStudentInfo[]> = new BehaviorSubject<BasicStudentInfo[]>(null);
    SelectedAdminCollegeIDSubject: BehaviorSubject<number> = new BehaviorSubject<number>(2);
    SelectedSchoolYearSubject: BehaviorSubject<string> = new BehaviorSubject<string>('');
    SelectedStudentSubject: BehaviorSubject<StudentInfo> = new BehaviorSubject<StudentInfo>(null);

    CollegeCourses: CollegeCoursesListInfo[];
    IssueAlerts: IssueAlertInfo[];

    // #region Getters and Setters for Subjects

    public get AllColleges(): CollegeInfo[] {
        return this.AllCollegesSubject.value;
    }

    public set AllColleges(value: CollegeInfo[]) {
        const data = (value || []).slice();

        this.AllCollegesSubject.next(data);
        this._AllColleges = data;
    }

    public get CurrentSchool(): string {
        return this.CurrentSchoolSubject.value;
    }

    public set CurrentSchool(value: string) {
        this.CurrentSchoolSubject.next(value);
        this._CurrentSchool = this.CurrentSchoolSubject.value;
    }

    public get CurrentSchoolYear(): string {
        return this.CurrentSchoolYearSubject.value;
    }
    public set CurrentSchoolYear(value: string) {
        this.CurrentSchoolYearSubject.next(value);
        this._CurrentSchoolYear = value;
    }

    public get DevSite(): boolean {
        return this.DevSiteSubject.value;
    }

    public set DevSite(value: boolean) {
        this.DevSiteSubject.next(value);
    }

    public get ParentStudentChildren(): BasicStudentInfo[] {
        return this.ParentStudentChildrenSubject.value;
    }

    public set ParentStudentChildren(value: BasicStudentInfo[]) {
        this.ParentStudentChildrenSubject.next(value);
    }

    public get SelectedAdminCollegeID(): number {
        return this.SelectedAdminCollegeIDSubject.value;
    }

    public set SelectedAdminCollegeID(value: number) {
        this.SelectedAdminCollegeIDSubject.next(value);
        this._SelectedAdminCollegeID = value;
    }

    public get SelectedSchoolYear(): string {
        return this.SelectedSchoolYearSubject.value;
    }

    public set SelectedSchoolYear(value: string) {
        this.SelectedSchoolYearSubject.next(value);
        this._SelectedSchoolYear = value;
    }

    public get SelectedStudent(): StudentInfo {
        return this.SelectedStudentSubject.value;
    }

    public set SelectedStudent(value: StudentInfo) {
        this.SelectedStudentSubject.next(value);
        this._SelectedStudent = value;
    }

    // #endregion

    constructor(private http: HttpClient, private configService: ConfigService, private storage: SessionStorageService) {
        if (this._AllColleges) {
            this.AllColleges = this._AllColleges;
        }
        if (this._CurrentSchool) {
            this.CurrentSchool = this._CurrentSchool;
        }
        if (this._CurrentSchoolYear) {
            this.CurrentSchoolYear = this._CurrentSchoolYear;
        }
        if (this._SelectedAdminCollegeID) {
            this.SelectedAdminCollegeID = this._SelectedAdminCollegeID;
        }
        if (this._SelectedSchoolYear) {
            this.SelectedSchoolYear = this._SelectedSchoolYear;
        }
        if (this._SelectedStudent) {
            this.SelectedStudent = this._SelectedStudent;
        }

        this.DevSite = window.location.hostname.indexOf('localhost') >= 0 || window.location.hostname.indexOf('deltadev') >= 0;
    }

    // #region Add Adoption Request Service Call

    AddAdoptionRequest(AuthToken: string, CurrentRole: string, Request: AdoptionRequestInfo, Colleges?: number[]) {
        return this.http.post<number>(`${this.configService.hostAddress}Api/Services/DELTA/AddAdoptionRequest`, {
            AuthToken: AuthToken || '',
            CurrentRole: CurrentRole || '',
            Colleges: (Colleges || []).toString() || '',
            Request: Request || '',
        });
    }

    // #endregion

    // #region Add College Contact Service Call

    AddCollegeContact(AuthToken: string, CollegeID: number, Email: string) {
        return this.http.post<CollegeContactInfo[]>(
            this.configService.hostAddress +`Api/Services/DELTA/AddCollegeContact`, {
                AuthToken: AuthToken || '',
                CollegeID: CollegeID || '',
                Email: Email || '',
            }
        );
    }

    // #endregion

    // #region Add College Staff To College Service Call

    AddCollegeStaffToCollege(AuthToken: string, UserID?: number, CollegeID?: number, AccessLevel?: number) {
        return this.http.get<CollegeStaff[]>(
            this.configService.hostAddress +
                `Api/Services/DELTA/AddCollegeStaffToCollege?AuthToken=${AuthToken || ''}&UserID=${UserID || ''}&CollegeID=${
                    CollegeID || ''
                }&AccessLevel=${AccessLevel != null ? AccessLevel : ''}`
        );
    }

    // #endregion

    // #region Add Eligible Course Service Call

    AddEligibleCourse(AuthToken: string, Prefix: string, Code: string, Colleges: number[]) {
        return this.http.get<EligibleCourseInfo[]>(
            this.configService.hostAddress +
                `Api/Services/DELTA/AddEligibleCourse?AuthToken=${AuthToken || ''}&Prefix=${Prefix || ''}&Code=${Code || ''}&Colleges=${Colleges || ''}`
        );
    }

    // #endregion

    // #region Add Eligible Course College Service Call

    AddEligibleCourseCollege(AuthToken: string, EligibleCourseID?: number, CollegeID?: number) {
        return this.http.post<EligibleCourseInfo[]>(this.configService.hostAddress + `Api/Services/DELTA/AddEligibleCourseCollege`, {
            AuthToken: AuthToken || '',
            EligibleCourseID: EligibleCourseID,
            CollegeID: CollegeID,
        });
    }

    // #endregion

    // #region Add Term Window To College Service Call

    AddTermWindowToCollege(
        AuthToken: string,
        CollegeID: number,
        CollegeSchoolYear: string,
        TermCode: string,
        DateBegin: Date,
        DateEnd: Date
    ) {
        return this.http
            .get<CollegeTermInfo[]>(
                this.configService.hostAddress +
                    `Api/Services/DELTA/AddTermWindowToCollege?AuthToken=${AuthToken || ''}&CollegeID=${
                        CollegeID || ''
                    }&CollegeSchoolYear=${CollegeSchoolYear || ''}&TermCode=${TermCode || ''}&DateBegin=${
                        DateBegin != null ? DateBegin.toJSON() : ''
                    }&DateEnd=${DateEnd != null ? DateEnd.toJSON() : ''}`
            )
            .pipe(
                map((res) => {
                    const resdata = res || [];

                    resdata.forEach((data) => {
                        data.DateBegin = data.DateBegin != null ? new Date(data.DateBegin) : null;
                        data.DateEnd = data.DateEnd != null ? new Date(data.DateEnd) : null;
                    });

                    return resdata;
                })
            );
    }

    // #endregion

    // #region Add Student College Record Service Call

    AddStudentCollegeRecord(
        AuthToken: string,
        StudentID: string,
        UserID: number,
        SchoolYear: string,
        TermWindowID: number,
        CollegeID: number,
        CounselorSAPID: string,
        FormStatus: number
    ) {
        const self = this;

        // Preparse the SchoolYear parameter here. If it is YYYYZZZZ format, bring it down to YYZZ before sending along.
        if (SchoolYear.length === 8) {
            SchoolYear = SchoolYear.substr(2, 2) + SchoolYear.substr(6, 2);
        }

        return this.http
            .get<AddStudentCollegeRecordReturn>(
                this.configService.hostAddress +
                    `Api/Services/DELTA/AddStudentCollegeRecord?AuthToken=${AuthToken || ''}&StudentID=${StudentID || ''}&UserID=${
                        UserID || ''
                    }&SchoolYear=${SchoolYear || ''}&TermWindowID=${TermWindowID || ''}&CollegeID=${CollegeID || ''}&CounselorSAPID=${
                        CounselorSAPID || ''
                    }&FormStatus=${FormStatus || ''}`
            )
            .pipe(
                map((res) => {
                    // Establish the original counselor SAPID before handing back to client
                    if (res && res.Requests && res.Requests.length) {
                        res.Requests.forEach((data) => {
                            data.OriginalCounselorSAPID = data.CounselorSAPID;
                            data.DualEnrollmentProgram = self.GetDualEnrollmentProgramInfo(data.DualEnrollmentProgramID);
                            data.OriginalDualEnrollmentProgramID = data.DualEnrollmentProgramID;
                            data.OriginalAcceleratedDegreeProgramID = data.AcceleratedDegreeProgramID;
                            data.AvailableTerms = data.AvailableTerms || [];
                            data.AnyOpenTerms =
                                data.AvailableTerms.findIndex((item) => {
                                    return item.CurrentlyAvailable === true;
                                }) >= 0;

                            // Process all date objects into dates
                            data.AgreementTextDate = data.AgreementTextDate ? new Date(data.AgreementTextDate) : null;
                            data.CollegeApprovedDate = data.CollegeApprovedDate ? new Date(data.CollegeApprovedDate) : null;
                            data.CounselorApprovedDate = data.CounselorApprovedDate ? new Date(data.CounselorApprovedDate) : null;
                            data.DateUpdated = data.DateUpdated ? new Date(data.DateUpdated) : null;
                            data.ParentApprovedDate = data.ParentApprovedDate ? new Date(data.ParentApprovedDate) : null;
                            data.SchoolApprovedDate = data.SchoolApprovedDate ? new Date(data.SchoolApprovedDate) : null;

                            if (data.Term) {
                                data.Term.DateBegin = data.Term.DateBegin ? new Date(data.Term.DateBegin) : null;
                                data.Term.DateEnd = data.Term.DateEnd ? new Date(data.Term.DateEnd) : null;
                            }

                            if (data.AvailableTerms && data.AvailableTerms.length) {
                                data.AvailableTerms.forEach((tdata) => {
                                    tdata.DateBegin = tdata.DateBegin ? new Date(tdata.DateBegin) : null;
                                    tdata.DateEnd = tdata.DateEnd ? new Date(tdata.DateEnd) : null;
                                });
                            }
                        });
                    }

                    return res;
                })
            );
    }

    // #endregion

    // #region Add Student College Record Course Service Call

    AddStudentCollegeRecordCourse(
        AuthToken: string,
        DualEnrollStudentID: number,
        CourseID: number,
        AddedBy: number,
        PriorityRequest: boolean
    ) {
        return this.http
            .get<CourseNotesInfo>(
                this.configService.hostAddress +
                    `Api/Services/DELTA/AddStudentCollegeRecordCourse?AuthToken=${AuthToken || ''}&DualEnrollStudentID=${
                        DualEnrollStudentID || ''
                    }&CourseID=${CourseID || ''}&AddedBy=${AddedBy || ''}&PriorityRequest=${PriorityRequest || ''}`
            )
            .pipe(
                map((res) => {
                    if (res) {
                        const data = res.Requests;

                        if (data && data.length) {
                            data.forEach((d) => {
                                // Create the full coded name for each course choice returned
                                d.FullCode = d.Prefix + ' ' + d.Code;

                                d.DateCreated = d.DateCreated ? new Date(d.DateCreated) : null;
                                d.DateRemoved = d.DateRemoved ? new Date(d.DateRemoved) : null;
                                d.DateScheduled = d.DateScheduled ? new Date(d.DateScheduled) : null;
                            });
                        }

                        if (res.Notes && res.Notes.length) {
                            res.Notes.forEach((note) => {
                                note.NoteDate = note.NoteDate ? new Date(note.NoteDate) : null;
                            });
                        }
                    }

                    return res;
                })
            );
    }

    // #endregion

    // #region Add Student College Record Note Service Call

    AddStudentCollegeRecordNote(AuthToken: string, DualEnrollStudentID: number, Note: string, NoteVisible: boolean) {
        return this.http
            .post<CollegeRecordNote[]>(this.configService.hostAddress + `Api/Services/DELTA/AddStudentCollegeRecordNote`, {
                AuthToken: AuthToken,
                DualEnrollStudentID: DualEnrollStudentID,
                Note: Note,
                NoteVisible: NoteVisible,
            })
            .pipe(
                map((res) => {
                    const data = (res || []).slice();

                    data.forEach((item) => {
                        item.NoteDate = item.NoteDate ? new Date(item.NoteDate) : null;
                    });

                    return data;
                })
            );
    }

    // #endregion

    // #region Add Web App Issue Alert Service Call

    AddWebAppIssueAlert(AuthToken: string, CurrentRole: string, AlertMessage: string, DateBegin: Date, DateEnd: Date) {
        return this.http
            .post<IssueAlertInfo[]>(`${this.configService.hostAddress}Api/Services/DELTA/AddWebAppIssueAlert`, {
                AuthToken: AuthToken || '',
                CurrentRole: CurrentRole || '',
                AlertMessage: AlertMessage || '',
                DateBegin: DateBegin ? DateBegin.toISOString() : '',
                DateEnd: DateEnd ? DateEnd.toISOString() : '',
            })
            .pipe(
                map((alerts) => {
                    if (alerts && !!alerts.length) {
                        alerts.forEach((alert) => {
                            alert.DateBegin = alert.DateBegin ? new Date(alert.DateBegin) : null;
                            alert.DateEnd = alert.DateEnd ? new Date(alert.DateEnd) : null;
                            alert.LastUpdated = alert.LastUpdated ? new Date(alert.LastUpdated) : null;
                        });
                    }

                    return alerts;
                })
            );
    }

    // #endregion

    // #region Add Web App Issue Type Service Call

    AddWebAppIssueType(AuthToken: string, CurrentRole: string, DisplayText: string, RequiresDescription: boolean) {
        return this.http.post<WebAppIssueInfo[]>(`${this.configService.hostAddress}Api/Services/DELTA/AddWebAppIssueType`, {
            AuthToken: AuthToken || '',
            CurrentRole: CurrentRole || '',
            DisplayText: DisplayText || '',
            RequiresDescription: RequiresDescription || '',
        });
    }

    // #endregion

    // #region Advisor Update Student College Record Service Call

    AdvisorUpdateStudentCollegeRecord(
        AuthToken: string,
        StudentID: string,
        DualEnrollStudentID: number,
        StaffID: number,
        FormStatus: number,
        Approved: boolean,
        ReturnNote: string
    ) {
        return this.http
            .post<StudentCollegeInfo[]>(this.configService.hostAddress + `Api/Services/DELTA/AdvisorUpdateStudentCollegeRecord`, {
                AuthToken: AuthToken || '',
                StudentID: StudentID || '',
                DualEnrollStudentID: DualEnrollStudentID || '',
                StaffID: StaffID || '',
                FormStatus: FormStatus || '',
                Approved: Approved != null ? Approved : '',
                ReturnNote: ReturnNote || '',
            })
            .pipe(
                map((res) => {
                    // Establish the original counselor SAPID before handing back to client
                    if (res && res.length) {
                        res.forEach((data) => {
                            data.OriginalCounselorSAPID = data.CounselorSAPID;
                            data.DualEnrollmentProgram = this.GetDualEnrollmentProgramInfo(data.DualEnrollmentProgramID);
                            data.OriginalDualEnrollmentProgramID = data.DualEnrollmentProgramID;
                            data.OriginalAcceleratedDegreeProgramID = data.AcceleratedDegreeProgramID;
                            data.AvailableTerms = data.AvailableTerms || [];
                            data.AnyOpenTerms =
                                data.AvailableTerms.findIndex((item) => {
                                    return item.CurrentlyAvailable === true;
                                }) >= 0;

                            // Process all date objects into dates
                            data.AgreementTextDate = data.AgreementTextDate ? new Date(data.AgreementTextDate) : null;
                            data.CollegeApprovedDate = data.CollegeApprovedDate ? new Date(data.CollegeApprovedDate) : null;
                            data.CounselorApprovedDate = data.CounselorApprovedDate ? new Date(data.CounselorApprovedDate) : null;
                            data.DateUpdated = data.DateUpdated ? new Date(data.DateUpdated) : null;
                            data.ParentApprovedDate = data.ParentApprovedDate ? new Date(data.ParentApprovedDate) : null;
                            data.SchoolApprovedDate = data.SchoolApprovedDate ? new Date(data.SchoolApprovedDate) : null;

                            if (data.Term) {
                                data.Term.DateBegin = data.Term.DateBegin ? new Date(data.Term.DateBegin) : null;
                                data.Term.DateEnd = data.Term.DateEnd ? new Date(data.Term.DateEnd) : null;
                            }

                            if (data.AvailableTerms && data.AvailableTerms.length) {
                                data.AvailableTerms.forEach((d) => {
                                    d.DateBegin = d.DateBegin ? new Date(d.DateBegin) : null;
                                    d.DateEnd = d.DateEnd ? new Date(d.DateEnd) : null;
                                });
                            }
                        });
                    }

                    return res;
                })
            );
    }

    // #endregion

    // #region Advisor Update Student College Records Service Call

    AdvisorUpdateStudentCollegeRecords(
        AuthToken: string,
        StudentID: string,
        DualEnrollStudentID: number,
        StaffID: number,
        FormStatus: number,
        Approved: boolean,
        ReturnNote: string,
        Courses: CourseChoice[]
    ) {
        return this.http
            .post<StudentCollegeInfo[]>(this.configService.hostAddress + `Api/Services/DELTA/AdvisorUpdateStudentCollegeRecords`, {
                AuthToken: AuthToken || '',
                StudentID: StudentID || '',
                DualEnrollStudentID: DualEnrollStudentID || '',
                StaffID: StaffID || '',
                FormStatus: FormStatus || '',
                Approved: Approved != null ? Approved : '',
                ReturnNote: ReturnNote || '',
                Courses: Courses || '',
            })
            .pipe(
                map((res) => {
                    // Establish the original counselor SAPID before handing back to client
                    if (res && res.length) {
                        res.forEach((data) => {
                            data.OriginalCounselorSAPID = data.CounselorSAPID;
                            data.DualEnrollmentProgram = this.GetDualEnrollmentProgramInfo(data.DualEnrollmentProgramID);
                            data.OriginalDualEnrollmentProgramID = data.DualEnrollmentProgramID;
                            data.OriginalAcceleratedDegreeProgramID = data.AcceleratedDegreeProgramID;
                            data.AvailableTerms = data.AvailableTerms || [];
                            data.AnyOpenTerms =
                                data.AvailableTerms.findIndex((item) => {
                                    return item.CurrentlyAvailable === true;
                                }) >= 0;

                            // Process all date objects into dates
                            data.AgreementTextDate = data.AgreementTextDate ? new Date(data.AgreementTextDate) : null;
                            data.CollegeApprovedDate = data.CollegeApprovedDate ? new Date(data.CollegeApprovedDate) : null;
                            data.CounselorApprovedDate = data.CounselorApprovedDate ? new Date(data.CounselorApprovedDate) : null;
                            data.DateUpdated = data.DateUpdated ? new Date(data.DateUpdated) : null;
                            data.ParentApprovedDate = data.ParentApprovedDate ? new Date(data.ParentApprovedDate) : null;
                            data.SchoolApprovedDate = data.SchoolApprovedDate ? new Date(data.SchoolApprovedDate) : null;

                            if (data.Term) {
                                data.Term.DateBegin = data.Term.DateBegin ? new Date(data.Term.DateBegin) : null;
                                data.Term.DateEnd = data.Term.DateEnd ? new Date(data.Term.DateEnd) : null;
                            }

                            if (data.AvailableTerms && data.AvailableTerms.length) {
                                data.AvailableTerms.forEach((d) => {
                                    d.DateBegin = d.DateBegin ? new Date(d.DateBegin) : null;
                                    d.DateEnd = d.DateEnd ? new Date(d.DateEnd) : null;
                                });
                            }
                        });
                    }

                    return res;
                })
            );
    }

    // #endregion

    // #region Approve Adoption Request Service Call

    ApproveAdoptionRequest(AuthToken: string, CurrentRole: string, AdoptionRequestID: number) {
        return this.http
            .get<AdoptionRequestInfo[]>(
                `${this.configService.hostAddress}Api/Services/DELTA/ApproveAdoptionRequest?AuthToken=${AuthToken || ''}&CurrentRole=${
                    CurrentRole || ''
                }&AdoptionRequestID=${AdoptionRequestID || ''}`
            )
            .pipe(
                map((body) => {
                    if (body && body.length) {
                        body.forEach((item) => {
                            item.DateRequested = item.DateRequested ? new Date(item.DateRequested) : null;
                            item.DateReviewed = item.DateReviewed ? new Date(item.DateReviewed) : null;
                        });
                    }

                    return body;
                })
            );
    }

    // #endregion

    // #region Approve College Agreement Service Call

    ApproveCollegeAgreement(AuthToken: string, StudentID: string, CollegeID: number, SchoolYear: string, TermWindowID: number) {
        return this.http.get<boolean>(
            this.configService.hostAddress +
                `Api/Services/DELTA/ApproveCollegeAgreement?AuthToken=${AuthToken || ''}&StudentID=${StudentID || ''}&CollegeID=${
                    CollegeID || ''
                }&SchoolYear=${SchoolYear || ''}&TermWindowID=${TermWindowID || ''}`
        );
    }

    // #endregion

    // #region Approve Terms Agreements Service Call

    ApproveTermsAgreements(AuthToken: string, StudentID: string, CollegeID: number, SchoolYear: string, TermWindowID: number) {
        return this.http
            .get<CollegeAgreementApprovalInfo[]>(
                this.configService.hostAddress +
                    `Api/Services/DELTA/ApproveTermsAgreements?AuthToken=${AuthToken || ''}&StudentID=${StudentID || ''}&CollegeID=${
                        CollegeID || ''
                    }&SchoolYear=${SchoolYear || ''}&TermWindowID=${TermWindowID || ''}`
            )
            .pipe(
                map((terms) => {
                    if (terms && !!terms.length) {
                        terms.forEach((term) => {
                            term.DateApproved = term.DateApproved ? new Date(term.DateApproved) : null;
                        });
                    }

                    return terms;
                })
            );
    }

    // #endregion

    // #region Clear Selected Student Service Call

    ClearSelectedStudent(AuthToken?: string) {
        const self = this;

        self.SelectedStudent = null;
    }

    // #endregion

    // #region Deny Adoption Request Service Call

    DenyAdoptionRequest(AuthToken: string, CurrentRole: string, AdoptionRequestID: number) {
        return this.http
            .get<AdoptionRequestInfo[]>(
                `${this.configService.hostAddress}Api/Services/DELTA/DenyAdoptionRequest?AuthToken=${AuthToken || ''}&CurrentRole=${
                    CurrentRole || ''
                }&AdoptionRequestID=${AdoptionRequestID || ''}`
            )
            .pipe(
                map((body) => {
                    if (body && body.length) {
                        body.forEach((item) => {
                            item.DateRequested = item.DateRequested ? new Date(item.DateRequested) : null;
                            item.DateReviewed = item.DateReviewed ? new Date(item.DateReviewed) : null;
                        });
                    }

                    return body;
                })
            );
    }

    // #endregion

    // #region Get Accelerated Degree Programs Service Call

    GetAcceleratedDegreePrograms(AuthToken: string) {
        const self = this;

        if (!self._AcceleratedPrograms) {
            // Load what we need.
            return this.http
                .get<ProgramInfo[]>(
                    this.configService.hostAddress + `Api/Services/DELTA/GetAcceleratedDegreePrograms?AuthToken=${AuthToken || ''}`
                )
                .pipe(
                    tap((res) => {
                        self._AcceleratedPrograms = res;
                    })
                );
        } else {
            return of<ProgramInfo[]>(self._AcceleratedPrograms);
        }
    }

    // #endregion

    // #region Get Agreement Approval Text Service Call

    GetAgreementApprovalText(AuthToken: string, CollegeID: number, TermWindowID: number, StudentID: string) {
        return this.http
            .get<CollegeAgreement>(
                this.configService.hostAddress +
                    `Api/Services/DELTA/GetAgreementApprovalText?AuthToken=${AuthToken || ''}&CollegeID=${
                        CollegeID || ''
                    }&TermWindowID=${TermWindowID || ''}&StudentID=${StudentID || ''}`
            )
            .pipe(
                map((res) => {
                    if (!res) {
                        return res;
                    }

                    const data = <CollegeAgreement>JSON.parse(JSON.stringify(res));

                    data.DateCreated = data.DateCreated ? new Date(data.DateCreated) : null;
                    data.CollegeID = CollegeID;

                    return data;
                })
            );
    }

    // #endregion

    // #region Get All Adoption Requests Service Call

    GetAllAdoptionRequests(AuthToken: string, CurrentRole: string) {
        return this.http
            .get<AdoptionRequestInfo[]>(
                this.configService.hostAddress +
                    `Api/Services/DELTA/GetAllAdoptionRequests?AuthToken=${AuthToken || ''}&CurrentRole=${CurrentRole || ''}`
            )
            .pipe(
                map((body) => {
                    if (body && body.length) {
                        body.forEach((item) => {
                            item.DateRequested = item.DateRequested ? new Date(item.DateRequested) : null;
                            item.DateReviewed = item.DateReviewed ? new Date(item.DateReviewed) : null;
                        });
                    }

                    return body;
                })
            );
    }

    // #endregion

    // #region Get All College Staff For College Service Call

    GetAllCollegeStaffForCollege(AuthToken: string, CollegeID: number) {
        return this.http.get<CollegeStaff[]>(
            this.configService.hostAddress +
                `Api/Services/DELTA/GetAllCollegeStaffForCollege?AuthToken=${AuthToken || ''}&CollegeID=${CollegeID || ''}`
        );
    }

    // #endregion

    // #region Get All Colleges Service Call

    GetAllColleges(AuthToken?: string) {
        const self = this;

        if (!self._AllColleges) {
            // Load what we need.
            return this.http
                .get<CollegeInfo[]>(this.configService.hostAddress + `Api/Services/DELTA/GetAllColleges?AuthToken=${AuthToken || ''}`)
                .pipe(
                    tap((res) => {
                        self._AllColleges = res;
                    }
                ));
        } else {
            // Return what we loaded already.
            return of<CollegeInfo[]>(self._AllColleges);
        }
    }

    // #endregion

    // #region Get All College Contacts Service Call

    GetAllCollegeContacts(AuthToken: string): Observable<CollegeContactInfo[]> {
        return this.http.get<CollegeContactInfo[]>(this.configService.hostAddress + `Api/Services/DELTA/GetAllCollegeContacts?AuthToken=${AuthToken || ''}`);
    }

    // #endregion

    // #region Get All College Years Service Call

    GetAllCollegeYears() {
        const self = this;

        if (!self._AllCollegeYears) {
            // We do not have what we need loaded yet. Load it now.
            return this.http.get<string[]>(this.configService.hostAddress + `Api/Services/DELTA/GetAllSchoolYears`).pipe(
                map((res) => {
                    const years = res;

                    self._AllSchoolYears = years;

                    // Process a list of all possible college school years. By design, this will be in 20## format, and will
                    // extend to 1 year beyond the current PCPS year.
                    // This is to accommodate colleges which refer to their Fall terms as the following year. For example,
                    // a class running in Fall of the 2016 calendar year may be referred to as the "Fall 2017" college year.
                    const collegeYears = [];

                    // If we are in the spring term (January - June), add 1 to the year to ensure we are getting the next available year.
                    const today = new Date();
                    const offset = getMonth(today) <= 5 ? 1 : 0;

                    if (self._AllSchoolYears && self._AllSchoolYears.length) {
                        const endyear = parseInt(years[0].substr(2, 2), 10) + offset;
                        const beginyear = parseInt(years[years.length - 1].substr(2, 2), 10);

                        for (let ci = endyear; ci >= beginyear; ci--) {
                            const newYear = '20' + ('00' + ci).slice(-2);
                            collegeYears.push(newYear);
                        }
                    }

                    self._AllCollegeYears = collegeYears;

                    return collegeYears;
                })
            );
        } else {
            // We already have the list of all college years. Return it as an observable.
            return of<string[]>(self._AllCollegeYears);
        }
    }

    // #endregion

    // #region Get All Courses At College Service Call

    GetAllCoursesAtCollege(AuthToken: string, CollegeID: number, SchoolYear: string) {
        const self = this;

        if (self.CollegeCourses && self.CollegeCourses.length) {
            // Do we have a match to the requested combination already loaded?
            const foundCourses = self.CollegeCourses.findIndex((data) => {
                return data.CollegeID === CollegeID && data.SchoolYear === SchoolYear;
            });

            if (foundCourses >= 0) {
                // Send this back.
                return empty().pipe(startWith(foundCourses));
            }
        }

        // Either we have loaded courses info, but not for this one, or we have no loaded courses info at all. Either case, load them now.
        return self.http
            .get<CollegeCoursesListInfo>(
                this.configService.hostAddress +
                    `Api/Services/DELTA/GetAllCoursesAtCollege?AuthToken=${AuthToken || ''}&CollegeID=${CollegeID || ''}&SchoolYear=${
                        SchoolYear || ''
                    }`
            )
            .pipe(
                map((res) => {
                    if (!res) {
                        return null;
                    }

                    const coldata = <CollegeCoursesListInfo>{
                        CollegeID: res.CollegeID,
                        SchoolYear: res.SchoolYear,
                        Courses: [],
                        Departments: [],
                    };

                    if (res.Courses && res.Courses.length) {
                        res.Courses.forEach((data) => {
                            const datadata = <CollegeCourseInfo>{ ...data };

                            // Set up the full course code for each entry before setting it into the system.
                            datadata.FullCode = datadata.Prefix + ' ' + datadata.Code;

                            coldata.Courses.push(datadata);
                        });
                    }

                    if (res.Departments && res.Departments.length) {
                        res.Departments.forEach((data) => {
                            const datadata = <CollegeDepartmentInfo>{ ...data };

                            if (datadata.Courses && datadata.Courses.length) {
                                datadata.Courses.forEach((cdata) => {
                                    cdata.FullCode = cdata.Prefix + ' ' + cdata.Code;
                                });
                            }

                            coldata.Departments.push(datadata);
                        });
                    }

                    self.CollegeCourses = self.CollegeCourses || [];
                    self.CollegeCourses.push(coldata);

                    return self.CollegeCourses.length - 1;
                })
            );
    }

    // #endregion

    // #region Get All Designees Service Call

    GetAllDesignees(AuthToken: string, CurrentRole: string) {
        return this.http
            .get<DesigneeInfo[]>(
                this.configService.hostAddress +
                    `Api/Services/DELTA/GetAllDesignees?AuthToken=${AuthToken || ''}&CurrentRole=${CurrentRole || ''}`
            )
            .pipe(
                map((body) => {
                    if (body && body.length) {
                        body.forEach((item) => {
                            item.DateAdded = item.DateAdded ? new Date(item.DateAdded) : null;
                        });
                    }

                    return body;
                })
            );
    }

    // #endregion

    // #region Get All Eligible Courses Service Call

    GetAllEligibleCourses(AuthToken: string): Observable<EligibleCourseInfo[]> {
        return this.http.get<EligibleCourseInfo[]>(this.configService.hostAddress + `Api/Services/DELTA/GetAllEligibleCourses?AuthToken=${AuthToken || ''}`);
    }

    // #endregion

    // #region Get All Grad Requirements Service Call

    GetAllGradRequirements() {
        return this.http.get<GradRequirementInfo[]>(`${this.configService.hostAddress}Api/Services/DELTA/GetAllGradRequirements`);
    }

    // #endregion

    // #region Get All Issue Reports Service Call

    GetAllIssueReports(AuthToken: string, CurrentRole: string, OriginDate?: Date) {
        return this.http
            .get<IssueReportInfo[]>(
                `${this.configService.hostAddress}Api/Services/DELTA/GetAllIssueReports?AuthToken=${AuthToken || ''}&CurrentRole=${
                    CurrentRole || ''
                }&OriginDate=${OriginDate ? OriginDate.toJSON() : ''}`
            )
            .pipe(
                map((reports) => {
                    if (reports && !!reports.length) {
                        reports.forEach((rep) => {
                            rep.DateReported = rep.DateReported ? new Date(rep.DateReported) : null;
                            rep.ResolvedDate = rep.ResolvedDate ? new Date(rep.ResolvedDate) : null;
                        });
                    }

                    return reports;
                })
            );
    }

    // #endregion

    // #region Get All Schools Service Call

    GetAllSchools(AuthToken: string, SchoolYear?: string): Observable<SchoolInfo[]> {
        const self = this;

        if (!this._AllSchools) {
            // We do not have what we need loaded yet. Load it now.
            return this.http
                .get<SchoolInfo[]>(
                    this.configService.hostAddress +
                        `Api/Services/DELTA/GetAllSchools?AuthToken=${AuthToken || ''}&SchoolYear=${SchoolYear || ''}`
                )
                .pipe(
                    tap((res) => {
                        self._AllSchools = res;
                    })
                );
        } else {
            // We already have the list of all schools. Return it as an observable.
            return of<SchoolInfo[]>(self._AllSchools);
        }
    }

    // #endregion

    // #region Get All School Years Service Call

    GetAllSchoolYears() {
        const self = this;

        if (!self._AllSchoolYears) {
            // We do not have what we need loaded yet. Load it now.
            return this.http.get<string[]>(this.configService.hostAddress + `Api/Services/DELTA/GetAllSchoolYears`).pipe(
                tap((res) => {
                    const years = res;

                    self._AllSchoolYears = years;

                    // Process a list of all possible college school years. By design, this will be in 20## format, and will
                    // extend to 1 year beyond the current PCPS year.
                    // This is to accommodate colleges which refer to their Fall terms as the following year. For example,
                    // a class running in Fall of the 2016 calendar year may be referred to as the "Fall 2017" college year.
                    const collegeYears = [];

                    // If we are in the spring term (January - June), add 1 to the year to ensure we are getting the next available year.
                    const today = new Date();
                    const offset = getMonth(today) <= 5 ? 1 : 0;

                    if (self._AllSchoolYears && self._AllSchoolYears.length) {
                        const endyear = parseInt(years[0].substr(2, 2), 10) + offset;
                        const beginyear = parseInt(years[years.length - 1].substr(2, 2), 10);

                        for (let ci = endyear; ci >= beginyear; ci--) {
                            const newYear = '20' + ('00' + ci).slice(-2);
                            collegeYears.push(newYear);
                        }
                    }

                    self._AllCollegeYears = collegeYears;
                })
            );
        } else {
            // We already have the list of all school years. Return it as an observable.
            return of<string[]>(self._AllSchoolYears);
        }
    }

    // #endregion

    // #region Get All School Years For Adoptions Service Call

    GetAllSchoolYearsForAdoptions() {
        const self = this;

        if (!self._AllSchoolYearsForAdoptions) {
            // We do not have what we need loaded yet. Load it now.
            return this.http.get<string[]>(this.configService.hostAddress + `Api/Services/DELTA/GetAllSchoolYearsForAdoptions`).pipe(
                tap((res) => {
                    const years = res;

                    self._AllSchoolYearsForAdoptions = years;
                })
            );
        } else {
            // We already have the list of all school years. Return it as an observable.
            return of<string[]>(self._AllSchoolYearsForAdoptions);
        }
    }

    // #endregion

    // #region Get All Web App Issue Alerts Service Call

    GetAllWebAppIssueAlerts() {
        return this.http.get<IssueAlertInfo[]>(`${this.configService.hostAddress}Api/Services/DELTA/GetAllWebAppIssueAlerts`).pipe(
            map((alerts) => {
                if (alerts && !!alerts.length) {
                    alerts.forEach((alert) => {
                        alert.DateBegin = alert.DateBegin ? new Date(alert.DateBegin) : null;
                        alert.DateEnd = alert.DateEnd ? new Date(alert.DateEnd) : null;
                        alert.LastUpdated = alert.LastUpdated ? new Date(alert.LastUpdated) : null;
                    });
                }

                return alerts;
            })
        );
    }

    // #endregion

    // #region Get All Web App Issue Alerts In Effect Service Call

    GetAllWebAppIssueAlertsInEffect() {
        return this.http
            .get<IssueAlertInfo[]>(`${this.configService.hostAddress}Api/Services/DELTA/GetAllWebAppIssueAlertsInEffect`)
            .pipe(
                map((alerts) => {
                    if (alerts && !!alerts.length) {
                        alerts.forEach((alert) => {
                            alert.DateBegin = alert.DateBegin ? new Date(alert.DateBegin) : null;
                            alert.DateEnd = alert.DateEnd ? new Date(alert.DateEnd) : null;
                            alert.LastUpdated = alert.LastUpdated ? new Date(alert.LastUpdated) : null;
                        });
                    }

                    return alerts;
                })
            );
    }

    // #endregion

    // #region Get All Web App Issues Service Call

    GetAllWebAppIssues() {
        return this.http.get<WebAppIssueInfo[]>(`${this.configService.hostAddress}Api/Services/DELTA/GetAllWebAppIssues`);
    }

    // #endregion

    // #region Get Available Terms Service Call

    GetAvailableTerms(AuthToken: string, CollegeID?: number, SchoolYear?: string) {
        return this.http
            .get<CollegeTermInfo[]>(
                this.configService.hostAddress +
                    `Api/Services/DELTA/GetAvailableTerms?AuthToken=${AuthToken || ''}&CollegeID=${CollegeID || ''}&SchoolYear=${
                        SchoolYear || ''
                    }`
            )
            .pipe(
                map((res) => {
                    const data = res || [];

                    data.forEach((d) => {
                        d.DateBegin = d.DateBegin != null ? new Date(d.DateBegin) : null;
                        d.DateEnd = d.DateEnd != null ? new Date(d.DateEnd) : null;
                    });

                    return data;
                })
            );
    }

    // #endregion

    // #region Get College Agreement Text Service Call

    GetCollegeAgreementText(AuthToken: string, CollegeID: number) {
        return this.http
            .get<CollegeAgreement>(
                this.configService.hostAddress +
                    `Api/Services/DELTA/GetCollegeAgreementText?AuthToken=${AuthToken || ''}&CollegeID=${CollegeID || ''}`
            )
            .pipe(
                map((res) => {
                    const data = <CollegeAgreement>{
                        ConsentText: (res && res.ConsentText) || '',
                        Text: (res && res.Text) || '',
                        Version: res && res.Version,
                        DateCreated: res && res.DateCreated ? new Date(res.DateCreated) : null,
                        CollegeID: CollegeID,
                    };

                    return data;
                })
            );
    }

    // #endregion

    // #region Get College Course Info By ID Service Call

    GetCollegeCourseInfoByID(AuthToken: string, CollegeCourseID: number) {
        return this.http.get<CollegeCourseInfo>(
            `${this.configService.hostAddress}Api/Services/DELTA/GetCollegeCourseInfoByID?AuthToken=${
                AuthToken || ''
            }&CollegeCourseID=${CollegeCourseID || ''}`
        );
    }

    // #endregion

    // #region Get Current School Year Service Call

    GetCurrentSchoolYear(): Observable<string> {
        const self = this;

        if (!self._CurrentSchoolYear) {
            // We do not have what we need yet loaded. Load it now.
            return this.http.get<string>(this.configService.hostAddress + `Api/Services/DELTA/GetCurrentSchoolYear?AuthToken=`).pipe(
                tap((res) => {
                    const body = res;

                    self._CurrentSchoolYear = body;
                    if (!self._SelectedSchoolYear) {
                        self._SelectedSchoolYear = self._CurrentSchoolYear;
                    }

                    // One more thing. If the current date is June 1 - July 15, AND the current school year reported by
                    // the system is not the "next" school year, turn on the summer rollover note alert so people won't
                    // panic about the school year thing.
                    const today = new Date();
                    const june1 = parse(`${getYear(today)}-06-01`);
                    const july15 = endOfDay(parse(`${getYear(today)}-07-15`));

                    if (isWithinRange(today, june1, july15)) {
                        // Check the current year string against the year which just ended.
                        const yearnum = getYear(today) % 1000;
                        const lystr = ('00' + (yearnum - 1)).slice(-2) + ('00' + yearnum).slice(-2);

                        // Set the global appvar accordingly
                        // Show the note if the current year reported still matches the apparent last year
                        self.ShowSummerRolloverNote = self._CurrentSchoolYear === lystr;
                    }
                })
            );
        } else {
            // We already have the current school year. Return it as an observable.
            return of<string>(self._CurrentSchoolYear);
        }
    }

    // #endregion

    // #region Get Designee Counselor Rights Service Call

    GetDesigneeCounselorRights(AuthToken: string) {
        return this.http.get<DesigneeRightsInfo[]>(
            this.configService.hostAddress + `Api/Services/DELTA/GetDesigneeCounselorRights?AuthToken=${AuthToken || ''}`
        );
    }

    // #endregion

    // #region Get Dual Enrollment Program Info Service Call

    GetDualEnrollmentProgramInfo(DualEnrollmentProgramID: number) {
        if (DualEnrollmentProgramID == null) {
            return null;
        }

        if (!this._DualEnrollmentPrograms || !this._DualEnrollmentPrograms.length) {
            return null;
        }

        return (
            this._DualEnrollmentPrograms.find((data) => {
                return data.ProgramID === DualEnrollmentProgramID;
            }) || null
        );
    }

    // #endregion

    // #region Get Dual Enrollment Programs Service Call

    GetDualEnrollmentPrograms(AuthToken: string) {
        const self = this;

        if (!self._DualEnrollmentPrograms) {
            // Load what we need.
            return this.http
                .get<ProgramInfo[]>(
                    this.configService.hostAddress + `Api/Services/DELTA/GetDualEnrollmentPrograms?AuthToken=${AuthToken || ''}`
                )
                .pipe(
                    map((res) => {
                        if (res && res.length) {
                            res.forEach((data) => {
                                data.CreditsText =
                                    data.MinimumCredits +
                                    (data.MaximumCredits != null ? ' - ' + data.MaximumCredits : '+') +
                                    ' credits';
                            });
                        }

                        return res;
                    }),
                    tap((res) => {
                        self._DualEnrollmentPrograms = res;
                    })
                );
        } else {
            return of<ProgramInfo[]>(self._DualEnrollmentPrograms);
        }
    }

    // #endregion

    // #region Get Parent Contact Information Service Call

    GetParentContactInformation(AuthToken: string) {
        return this.http.get<ParentInfo>(
            this.configService.hostAddress + `Api/Services/DELTA/GetParentContactInformation?AuthToken=${AuthToken || ''}`
        );
    }

    // #endregion

    // #region Get School Counselors Service Call

    GetSchoolCounselors(AuthToken: string, SchoolNumber: string) {
        return this.http.get<SchoolCounselorInfo[]>(
            this.configService.hostAddress +
                `Api/Services/DELTA/GetSchoolCounselors?AuthToken=${AuthToken || ''}&SchoolNumber=${SchoolNumber || ''}`
        );
    }

    // #endregion

    // #region Get Student College Information Service Call

    GetStudentCollegeInformation(AuthToken: string, StudentID: string) {
        const self = this;

        return this.http
            .get<StudentCollegeInfo[]>(
                this.configService.hostAddress +
                    `Api/Services/DELTA/GetStudentCollegeInformation?AuthToken=${AuthToken || ''}&StudentID=${StudentID || ''}`
            )
            .pipe(
                map((res) => {
                    if (!res) {
                        return res;
                    }

                    const resdata = <StudentCollegeInfo[]>JSON.parse(JSON.stringify(res));

                    resdata.forEach((data) => {
                        // Establish the original counselor SAPID before handing back to client
                        data.OriginalCounselorSAPID = data.CounselorSAPID;
                        data.DualEnrollmentProgram = self.GetDualEnrollmentProgramInfo(data.DualEnrollmentProgramID);
                        data.OriginalDualEnrollmentProgramID = data.DualEnrollmentProgramID;
                        data.OriginalAcceleratedDegreeProgramID = data.AcceleratedDegreeProgramID;
                        data.AvailableTerms = data.AvailableTerms || [];
                        data.AnyOpenTerms =
                            data.AvailableTerms.findIndex((item) => {
                                return item.CurrentlyAvailable === true;
                            }) >= 0;

                        // Process all date objects into dates
                        data.AgreementTextDate = data.AgreementTextDate ? new Date(data.AgreementTextDate) : null;
                        data.CollegeApprovedDate = data.CollegeApprovedDate ? new Date(data.CollegeApprovedDate) : null;
                        data.CounselorApprovedDate = data.CounselorApprovedDate ? new Date(data.CounselorApprovedDate) : null;
                        data.DateUpdated = data.DateUpdated ? new Date(data.DateUpdated) : null;
                        data.ParentApprovedDate = data.ParentApprovedDate ? new Date(data.ParentApprovedDate) : null;
                        data.SchoolApprovedDate = data.SchoolApprovedDate ? new Date(data.SchoolApprovedDate) : null;

                        if (data.Term) {
                            data.Term.DateBegin = data.Term.DateBegin ? new Date(data.Term.DateBegin) : null;
                            data.Term.DateEnd = data.Term.DateEnd ? new Date(data.Term.DateEnd) : null;
                        }

                        if (data.AvailableTerms && data.AvailableTerms.length) {
                            data.AvailableTerms.forEach((d) => {
                                d.DateBegin = d.DateBegin ? new Date(d.DateBegin) : null;
                                d.DateEnd = d.DateEnd ? new Date(d.DateEnd) : null;
                            });
                        }
                    });

                    return resdata;
                })
            );
    }

    // #endregion

    // #region Get Student College Record Notes Service Call

    GetStudentCollegeRecordNotes(AuthToken: string, DualEnrollStudentID: number) {
        return this.http
            .get<CollegeRecordNote[]>(
                this.configService.hostAddress +
                    `Api/Services/DELTA/GetStudentCollegeRecordNotes?AuthToken=${AuthToken || ''}&DualEnrollStudentID=${
                        DualEnrollStudentID || ''
                    }`
            )
            .pipe(
                map((notes) => {
                    if (!notes || !notes.length) {
                        return notes;
                    }

                    notes.forEach((data) => {
                        data.NoteDate = data.NoteDate != null ? new Date(data.NoteDate) : null;
                    });

                    return notes;
                })
            );
    }

    // #endregion

    // #region Get Student Course Choices Service Call

    GetStudentCourseChoices(AuthToken: string, DualEnrollStudentID: number) {
        return this.http
            .get<CourseChoice[]>(
                this.configService.hostAddress +
                    `Api/Services/DELTA/GetStudentCourseChoices?AuthToken=${AuthToken || ''}&DualEnrollStudentID=${
                        DualEnrollStudentID || ''
                    }`
            )
            .pipe(
                map((res) => {
                    const data = (res || []).slice();

                    data.forEach((d) => {
                        // Create the full coded name for each course choice returned
                        d.FullCode = d.Prefix + ' ' + d.Code;

                        d.DateCreated = d.DateCreated ? new Date(d.DateCreated) : null;
                        d.DateRemoved = d.DateRemoved ? new Date(d.DateRemoved) : null;
                        d.DateScheduled = d.DateScheduled ? new Date(d.DateScheduled) : null;
                    });

                    return data;
                })
            );
    }

    // #endregion

    // #region Get Student School Information Service Call

    GetStudentSchoolInformation(AuthToken: string, StudentID?: string) {
        return this.http
            .get<StudentInfo>(
                this.configService.hostAddress +
                    `Api/Services/DELTA/GetStudentSchoolInformation?AuthToken=${AuthToken || ''}&StudentID=${StudentID || ''}`
            )
            .pipe(
                map((res) => {
                    if (!res) {
                        return res;
                    }

                    const data = <StudentInfo>JSON.parse(JSON.stringify(res));

                    data.BirthDate = data.BirthDate ? new Date(data.BirthDate) : null;
                    if (data.TestHistory && data.TestHistory.length) {
                        data.TestHistory.forEach((d) => {
                            (d.Years || []).forEach((yr) => {
                                (yr.Subjects || []).forEach((s) => {
                                    (s.Exams || []).forEach((ex) => {
                                        ex.TestDate = ex.TestDate ? new Date(ex.TestDate) : null;
                                    });
                                });
                            });
                        });
                    }

                    return data;
                })
            );
    }

    // #endregion

    // #region Get Student School Information By Year Service Call

    GetStudentSchoolInformationByYear(AuthToken: string, StudentID: string, SchoolYear: string) {
        return this.http
            .get<StudentInfo>(
                this.configService.hostAddress +
                    `Api/Services/DELTA/GetStudentSchoolInformationByYear?AuthToken=${AuthToken || ''}&StudentID=${
                        StudentID || ''
                    }&SchoolYear=${SchoolYear || ''}`
            )
            .pipe(
                map((res) => {
                    if (!res) {
                        return res;
                    }

                    const data = <StudentInfo>JSON.parse(JSON.stringify(res));

                    data.BirthDate = data.BirthDate ? new Date(data.BirthDate) : null;
                    if (data.TestHistory && data.TestHistory.length) {
                        data.TestHistory.forEach((d) => {
                            (d.Years || []).forEach((yr) => {
                                (yr.Subjects || []).forEach((s) => {
                                    (s.Exams || []).forEach((ex) => {
                                        ex.TestDate = ex.TestDate ? new Date(ex.TestDate) : null;
                                    });
                                });
                            });
                        });
                    }

                    return data;
                })
            );
    }

    // #endregion

    // #region Get Students For Parent Service Call

    GetStudentsForParent(AuthToken: string) {
        return this.http
            .get<BasicStudentInfo[]>(
                this.configService.hostAddress + `Api/Services/DELTA/GetStudentsForParent?AuthToken=${AuthToken || ''}`
            )
            .pipe(
                tap((kids) => {
                    this.ParentStudentChildren = kids;
                })
            );
    }

    // #endregion

    // #region Get Test Thresholds

    GetTestThresholds(AuthToken: string) {
        return this.http
            .get<TestThresholdInfo[]>(
                this.configService.hostAddress + `Api/Services/DELTA/GetTestThresholds?AuthToken=${AuthToken || ''}`
            )
            .pipe(
                map((data) => {
                    if (data) {
                        data.forEach((d) => {
                            d.DateUpdated = d.DateUpdated ? new Date(d.DateUpdated) : null;
                        });
                    }

                    return data;
                })
            );
    }

    // #endregion

    // #region Parent Approve College Agreement Service Call

    ParentApproveCollegeAgreement(
        AuthToken: string,
        StudentID: string,
        ParentUserID: number,
        ParentFocusStaffID: number,
        DualEnrollStudentID: number,
        CollegeID: number,
        SchoolYear: string,
        TermWindowID: number,
        AsProxy: boolean,
        ProxyParentName: string,
        ProxyRelationship: number,
        ProxyDescription: string
    ) {
        return this.http
            .post<CollegeAgreementApprovalInfo[]>(
                this.configService.hostAddress + `Api/Services/DELTA/ParentApproveCollegeAgreement`,
                {
                    AuthToken: AuthToken || '',
                    StudentID: StudentID || '',
                    ParentUserID: ParentUserID || '',
                    ParentFocusStaffID: ParentFocusStaffID || '',
                    DualEnrollStudentID: DualEnrollStudentID || '',
                    CollegeID: CollegeID || '',
                    SchoolYear: SchoolYear || '',
                    TermWindowID: TermWindowID || '',
                    AsProxy: AsProxy || '',
                    ProxyParentName: ProxyParentName || '',
                    ProxyRelationship: ProxyRelationship || '',
                    ProxyDescription: ProxyDescription || '',
                }
            )
            .pipe(
                map((terms) => {
                    if (terms && !!terms.length) {
                        terms.forEach((term) => {
                            term.DateApproved = term.DateApproved ? new Date(term.DateApproved) : null;
                        });
                    }
                })
            );
    }

    // #endregion

    // #region Remove College Contact Service Call

    RemoveCollegeContact(AuthToken: string, CollegeContactID?: number) {
        return this.http.get<CollegeContactInfo[]>(this.configService.hostAddress + 
            `Api/Services/DELTA/RemoveCollegeContact?AuthToken=${AuthToken || ''}&CollegeContactID=${CollegeContactID || ''}`);
    }

    // #endregion

    // #region Remove College Staff From College Service Call

    RemoveCollegeStaffFromCollege(AuthToken: string, StaffID: number) {
        return this.http.get<CollegeStaff[]>(
            this.configService.hostAddress +
                `Api/Services/DELTA/RemoveCollegeStaffFromCollege?AuthToken=${AuthToken || ''}&StaffID=${StaffID || ''}`
        );
    }

    // #endregion

    // #region Remove Designee Service Call

    RemoveDesignee(AuthToken: string, CurrentRole: string, DesigneeID: number) {
        return this.http
            .get<DesigneeInfo[]>(
                this.configService.hostAddress +
                    `Api/Services/DELTA/RemoveDesignee?AuthToken=${AuthToken || ''}&CurrentRole=${CurrentRole || ''}&DesigneeID=${
                        DesigneeID || ''
                    }`
            )
            .pipe(
                map((body) => {
                    if (body && body.length) {
                        body.forEach((item) => {
                            item.DateAdded = item.DateAdded ? new Date(item.DateAdded) : null;
                        });
                    }

                    return body;
                })
            );
    }

    // #endregion

    // #region Remove Eligible Course Service Call

    RemoveEligibleCourse(AuthToken: string, ECourseID: number) {
        return this.http.get<EligibleCourseInfo[]>(
            this.configService.hostAddress +
                `Api/Services/DELTA/RemoveEligibleCourse?AuthToken=${AuthToken || ''}&ECourseID=${ECourseID || ''}`
        );
    }

    // #endregion

    // #region Remove Eligible Course College Service Call

    RemoveEligibleCourseCollege(AuthToken: string, EligibleCourseID?: number, CollegeID?: number) {
        return this.http.post<EligibleCourseInfo[]>(this.configService.hostAddress + `Api/Services/DELTA/RemoveEligibleCourseCollege`, {
            AuthToken: AuthToken || '',
            EligibleCourseID: EligibleCourseID || '',
            CollegeID: CollegeID || '',
        });
    }

    // #endregion

    // #region Remove Student College Record Service Call

    RemoveStudentCollegeRecord(AuthToken: string, StudentID: string, DualEnrollStudentID: number) {
        return this.http
            .get<StudentCollegeInfo[]>(
                this.configService.hostAddress +
                    `Api/Services/DELTA/RemoveStudentCollegeRecord?AuthToken=${AuthToken || ''}&StudentID=${
                        StudentID || ''
                    }&DualEnrollStudentID=${DualEnrollStudentID || ''}`
            )
            .pipe(
                map((res) => {
                    const resdata = (res || []).slice();

                    resdata.forEach((data) => {
                        data.OriginalCounselorSAPID = data.CounselorSAPID;
                        data.DualEnrollmentProgram = this.GetDualEnrollmentProgramInfo(data.DualEnrollmentProgramID);
                        data.OriginalDualEnrollmentProgramID = data.DualEnrollmentProgramID;
                        data.OriginalAcceleratedDegreeProgramID = data.AcceleratedDegreeProgramID;
                        data.AvailableTerms = data.AvailableTerms || [];
                        data.AnyOpenTerms =
                            data.AvailableTerms.findIndex((item) => {
                                return item.CurrentlyAvailable === true;
                            }) >= 0;

                        // Process all date objects into dates
                        data.AgreementTextDate = data.AgreementTextDate ? new Date(data.AgreementTextDate) : null;
                        data.CollegeApprovedDate = data.CollegeApprovedDate ? new Date(data.CollegeApprovedDate) : null;
                        data.CounselorApprovedDate = data.CounselorApprovedDate ? new Date(data.CounselorApprovedDate) : null;
                        data.DateUpdated = data.DateUpdated ? new Date(data.DateUpdated) : null;
                        data.ParentApprovedDate = data.ParentApprovedDate ? new Date(data.ParentApprovedDate) : null;
                        data.SchoolApprovedDate = data.SchoolApprovedDate ? new Date(data.SchoolApprovedDate) : null;

                        if (data.Term) {
                            data.Term.DateBegin = data.Term.DateBegin ? new Date(data.Term.DateBegin) : null;
                            data.Term.DateEnd = data.Term.DateEnd ? new Date(data.Term.DateEnd) : null;
                        }

                        if (data.AvailableTerms && data.AvailableTerms.length) {
                            data.AvailableTerms.forEach((d) => {
                                d.DateBegin = d.DateBegin ? new Date(d.DateBegin) : null;
                                d.DateEnd = d.DateEnd ? new Date(d.DateEnd) : null;
                            });
                        }
                    });

                    return resdata;
                })
            );
    }

    // #endregion

    // #region Remove Student College Record Course Service Call

    RemoveStudentCollegeRecordCourse(
        AuthToken: string,
        CurrentRole: string,
        DualEnrollStudentID: number,
        CourseChoiceID: number,
        RemovedBy: number
    ) {
        const self = this;

        return this.http
            .get<CourseNotesInfo>(
                this.configService.hostAddress +
                    `Api/Services/DELTA/RemoveStudentCollegeRecordCourse?AuthToken=${AuthToken || ''}&CurrentRole=${
                        CurrentRole || ''
                    }&DualEnrollStudentID=${DualEnrollStudentID || ''}&CourseChoiceID=${CourseChoiceID || ''}&RemovedBy=${
                        RemovedBy || ''
                    }`
            )
            .pipe(
                map((res) => {
                    if (res.Requests && res.Requests.length) {
                        res.Requests.forEach((data) => {
                            // Create the full coded name for each course choice returned
                            data.FullCode = data.Prefix + ' ' + data.Code;

                            data.DateCreated = data.DateCreated ? new Date(data.DateCreated) : null;
                            data.DateRemoved = data.DateRemoved ? new Date(data.DateRemoved) : null;
                            data.DateScheduled = data.DateScheduled ? new Date(data.DateScheduled) : null;
                        });
                    }

                    if (res.Notes && res.Notes.length) {
                        res.Notes.forEach((note) => {
                            note.NoteDate = note.NoteDate ? new Date(note.NoteDate) : null;
                        });
                    }

                    return res;
                })
            );
    }

    // #endregion

    // #region Remove Student College Record Note Service Call

    RemoveStudentCollegeRecordNote(AuthToken: string, DualEnrollStudentID: number, NoteID: number) {
        return this.http
            .get<CollegeRecordNote[]>(
                this.configService.hostAddress +
                    `Api/Services/DELTA/RemoveStudentCollegeRecordNote?AuthToken=${AuthToken || ''}&DualEnrollStudentID=${
                        DualEnrollStudentID || ''
                    }&NoteID=${NoteID || ''}`
            )
            .pipe(
                map((res) => {
                    const data = (res || []).slice();

                    data.forEach((item) => {
                        item.NoteDate = item.NoteDate ? new Date(item.NoteDate) : null;
                    });

                    return data;
                })
            );
    }

    // #endregion

    // #region Remove Term Window From College Service Call

    RemoveTermWindowFromCollege(AuthToken: string, TermWindowID: number) {
        return this.http
            .get<CollegeTermInfo[]>(
                this.configService.hostAddress +
                    `Api/Services/DELTA/RemoveTermWindowFromCollege?AuthToken=${AuthToken || ''}&TermWindowID=${TermWindowID || ''}`
            )
            .pipe(
                map((res) => {
                    const data = res || [];

                    data.forEach((d) => {
                        d.DateBegin = d.DateBegin != null ? new Date(d.DateBegin) : null;
                        d.DateEnd = d.DateEnd != null ? new Date(d.DateEnd) : null;
                    });

                    return data;
                })
            );
    }

    // #endregion

    // #region Remove Test Threshold

    RemoveTestThreshold(AuthToken: string, CurrentRole: string, TestThresholdID: number) {
        return this.http.get<number>(
            this.configService.hostAddress +
                `Api/Services/DELTA/RemoveTestThreshold?AuthToken=${AuthToken || ''}&CurrentRole=${
                    CurrentRole || ''
                }&TestThresholdID=${TestThresholdID || ''}`
        );
    }

    // #endregion

    // #region Remove Web App Issue Alert Service Call

    RemoveWebAppIssueAlert(AuthToken: string, CurrentRole: string, WebAppIssueAlertID: number) {
        return this.http
            .get<IssueAlertInfo[]>(
                `${this.configService.hostAddress}Api/Services/DELTA/RemoveWebAppIssueAlert?AuthToken=${AuthToken || ''}&CurrentRole=${
                    CurrentRole || ''
                }&WebAppIssueAlertID=${WebAppIssueAlertID || ''}`
            )
            .pipe(
                map((alerts) => {
                    if (alerts && !!alerts.length) {
                        alerts.forEach((alert) => {
                            alert.DateBegin = alert.DateBegin ? new Date(alert.DateBegin) : null;
                            alert.DateEnd = alert.DateEnd ? new Date(alert.DateEnd) : null;
                            alert.LastUpdated = alert.LastUpdated ? new Date(alert.LastUpdated) : null;
                        });
                    }

                    return alerts;
                })
            );
    }

    // #endregion

    // #region Remove Web App Issue Type Service Call

    RemoveWebAppIssueType(AuthToken: string, CurrentRole: string, IssueID: number) {
        return this.http.get<WebAppIssueInfo[]>(
            `${this.configService.hostAddress}Api/Services/DELTA/RemoveWebAppIssueType?AuthToken=${AuthToken || ''}&CurrentRole=${
                CurrentRole || ''
            }&IssueID=${IssueID || ''}`
        );
    }

    // #endregion

    // #region Resolve Issue Report Service Call

    ResolveIssueReport(AuthToken: string, CurrentRole: string, IssueID: number, ResolverComments?: string) {
        return this.http
            .post<IssueReportInfo[]>(`${this.configService.hostAddress}Api/Services/DELTA/ResolveIssueReport`, {
                AuthToken: AuthToken || '',
                CurrentRole: CurrentRole || '',
                IssueID: IssueID || '',
                ResolverComments: ResolverComments || '',
            })
            .pipe(
                map((reports) => {
                    if (reports && !!reports.length) {
                        reports.forEach((rep) => {
                            rep.DateReported = rep.DateReported ? new Date(rep.DateReported) : null;
                            rep.ResolvedDate = rep.ResolvedDate ? new Date(rep.ResolvedDate) : null;
                        });
                    }

                    return reports;
                })
            );
    }

    // #endregion

    // #region Schedule Student College Record Course Service Call

    ScheduleStudentCollegeRecordCourse(
        AuthToken: string,
        DualEnrollStudentID: number,
        CourseChoiceID: number,
        ScheduledBy: number,
        ReferenceNumber: string,
        CountyTaught: boolean
    ) {
        return this.http
            .get<CourseNotesInfo>(
                this.configService.hostAddress +
                    `Api/Services/DELTA/ScheduleStudentCollegeRecordCourse?AuthToken=${AuthToken || ''}&DualEnrollStudentID=${
                        DualEnrollStudentID || ''
                    }&CourseChoiceID=${CourseChoiceID || ''}&ScheduledBy=${ScheduledBy || ''}&ReferenceNumber=${
                        ReferenceNumber || ''
                    }&CountyTaught=${CountyTaught != null ? CountyTaught : ''}`
            )
            .pipe(
                map((res) => {
                    if (res) {
                        const data = res.Requests;

                        if (data && !!data.length) {
                            data.forEach((d) => {
                                // Create the full coded name for each course choice returned
                                d.FullCode = d.Prefix + ' ' + d.Code;

                                d.DateCreated = d.DateCreated ? new Date(d.DateCreated) : null;
                                d.DateRemoved = d.DateRemoved ? new Date(d.DateRemoved) : null;
                                d.DateScheduled = d.DateScheduled ? new Date(d.DateScheduled) : null;
                            });
                        }

                        if (res.Notes && res.Notes.length) {
                            res.Notes.forEach((note) => {
                                note.NoteDate = note.NoteDate ? new Date(note.NoteDate) : null;
                            });
                        }
                    }

                    return res;
                })
            );
    }

    // #endregion

    // #region School Admin Update Student College Record Service Call

    SchoolAdminUpdateStudentCollegeRecord(
        AuthToken: string,
        StudentID: string,
        DualEnrollStudentID: number,
        AdminSAPID: string,
        FormStatus: number
    ) {
        return this.http
            .get<StudentCollegeInfo[]>(
                this.configService.hostAddress +
                    `Api/Services/DELTA/SchoolAdminUpdateStudentCollegeRecord?AuthToken=${AuthToken || ''}&StudentID=${
                        StudentID || ''
                    }&DualEnrollStudentID=${DualEnrollStudentID || ''}&AdminSAPID=${AdminSAPID || ''}&FormStatus=${FormStatus || ''}`
            )
            .pipe(
                map((res) => {
                    const resdata = (res || []).slice();

                    resdata.forEach((data) => {
                        data.OriginalCounselorSAPID = data.CounselorSAPID;
                        data.DualEnrollmentProgram = this.GetDualEnrollmentProgramInfo(data.DualEnrollmentProgramID);
                        data.OriginalDualEnrollmentProgramID = data.DualEnrollmentProgramID;
                        data.OriginalAcceleratedDegreeProgramID = data.AcceleratedDegreeProgramID;
                        data.AvailableTerms = data.AvailableTerms || [];
                        data.AnyOpenTerms =
                            data.AvailableTerms.findIndex((item) => {
                                return item.CurrentlyAvailable === true;
                            }) >= 0;

                        // Process all date objects into dates
                        data.AgreementTextDate = data.AgreementTextDate ? new Date(data.AgreementTextDate) : null;
                        data.CollegeApprovedDate = data.CollegeApprovedDate ? new Date(data.CollegeApprovedDate) : null;
                        data.CounselorApprovedDate = data.CounselorApprovedDate ? new Date(data.CounselorApprovedDate) : null;
                        data.DateUpdated = data.DateUpdated ? new Date(data.DateUpdated) : null;
                        data.ParentApprovedDate = data.ParentApprovedDate ? new Date(data.ParentApprovedDate) : null;
                        data.SchoolApprovedDate = data.SchoolApprovedDate ? new Date(data.SchoolApprovedDate) : null;

                        if (data.Term) {
                            data.Term.DateBegin = data.Term.DateBegin ? new Date(data.Term.DateBegin) : null;
                            data.Term.DateEnd = data.Term.DateEnd ? new Date(data.Term.DateEnd) : null;
                        }

                        if (data.AvailableTerms && data.AvailableTerms.length) {
                            data.AvailableTerms.forEach((d) => {
                                d.DateBegin = d.DateBegin ? new Date(d.DateBegin) : null;
                                d.DateEnd = d.DateEnd ? new Date(d.DateEnd) : null;
                            });
                        }
                    });

                    return resdata;
                })
            );
    }

    // #endregion

    // #region School Counselor Update Student College Record Service Call

    SchoolCounselorUpdateStudentCollegeRecord(
        AuthToken: string,
        StudentID: string,
        DualEnrollStudentID: number,
        CounselorSAPID: string,
        FormStatus: number,
        AsAdminBypass?: boolean
    ) {
        return this.http
            .get<StudentCollegeInfo[]>(
                this.configService.hostAddress +
                    `Api/Services/DELTA/SchoolCounselorUpdateStudentCollegeRecord?AuthToken=${AuthToken || ''}&StudentID=${
                        StudentID || ''
                    }&DualEnrollStudentID=${DualEnrollStudentID || ''}&CounselorSAPID=${CounselorSAPID || ''}&FormStatus=${
                        FormStatus || ''
                    }&BypassAdmin=${AsAdminBypass || ''}`
            )
            .pipe(
                map((res) => {
                    const resdata = (res || []).slice();

                    resdata.forEach((data) => {
                        data.OriginalCounselorSAPID = data.CounselorSAPID;
                        data.DualEnrollmentProgram = this.GetDualEnrollmentProgramInfo(data.DualEnrollmentProgramID);
                        data.OriginalDualEnrollmentProgramID = data.DualEnrollmentProgramID;
                        data.OriginalAcceleratedDegreeProgramID = data.AcceleratedDegreeProgramID;
                        data.AvailableTerms = data.AvailableTerms || [];
                        data.AnyOpenTerms =
                            data.AvailableTerms.findIndex((item) => {
                                return item.CurrentlyAvailable === true;
                            }) >= 0;

                        // Process all date objects into dates
                        data.AgreementTextDate = data.AgreementTextDate ? new Date(data.AgreementTextDate) : null;
                        data.CollegeApprovedDate = data.CollegeApprovedDate ? new Date(data.CollegeApprovedDate) : null;
                        data.CounselorApprovedDate = data.CounselorApprovedDate ? new Date(data.CounselorApprovedDate) : null;
                        data.DateUpdated = data.DateUpdated ? new Date(data.DateUpdated) : null;
                        data.ParentApprovedDate = data.ParentApprovedDate ? new Date(data.ParentApprovedDate) : null;
                        data.SchoolApprovedDate = data.SchoolApprovedDate ? new Date(data.SchoolApprovedDate) : null;

                        if (data.Term) {
                            data.Term.DateBegin = data.Term.DateBegin ? new Date(data.Term.DateBegin) : null;
                            data.Term.DateEnd = data.Term.DateEnd ? new Date(data.Term.DateEnd) : null;
                        }

                        if (data.AvailableTerms && data.AvailableTerms.length) {
                            data.AvailableTerms.forEach((d) => {
                                d.DateBegin = d.DateBegin ? new Date(d.DateBegin) : null;
                                d.DateEnd = d.DateEnd ? new Date(d.DateEnd) : null;
                            });
                        }
                    });

                    return resdata;
                })
            );
    }

    // #endregion

    // #region Search For College Student Service Call

    SearchForCollegeStudent(
        AuthToken: string,
        SchoolYear: string,
        StudentID?: string,
        CollegeID?: number,
        CollegeStudentID?: string,
        Term?: string,
        SchoolYear_SIS?: string,
        GradeLevel?: string,
        FirstName?: string,
        LastName?: string,
        SchoolNumber?: string
    ) {
        return this.http
            .post<StudentSearchResult[]>(this.configService.hostAddress + `Api/Services/DELTA/SearchForCollegeStudent`, {
                AuthToken: AuthToken || '',
                SchoolYear: SchoolYear || '',
                StudentID: StudentID || '',
                CollegeID: CollegeID || '',
                CollegeStudentID: CollegeStudentID || '',
                Term: Term || '',
                SchoolYear_SIS: SchoolYear_SIS || '',
                GradeLevel: GradeLevel || '',
                FirstName: FirstName || '',
                LastName: LastName || '',
                SchoolNumber: SchoolNumber || '',
            })
            .pipe(
                map((res) => {
                    const body = <StudentSearchResult[]>(res || []);

                    body.forEach((item) => {
                        item.BirthDate = item.BirthDate ? new Date(item.BirthDate) : null;
                    });

                    return body;
                })
            );
    }

    // #endregion

    // #region Search For Student Service Call

    SearchForStudent(
        AuthToken: string,
        CurrentRole: string,
        SchoolYear: string,
        SchoolNumber: string,
        StudentID?: string,
        CollegeID?: number,
        Term?: string,
        SchoolYear_SIS?: string,
        GradeLevel?: string,
        FirstName?: string,
        LastName?: string
    ) {
        return this.http
            .post<StudentSearchResult[]>(this.configService.hostAddress + `Api/Services/DELTA/SearchForStudent`, {
                AuthToken: AuthToken || '',
                CurrentRole: CurrentRole || '',
                SchoolYear: SchoolYear || '',
                SchoolNumber: SchoolNumber || '',
                StudentID: StudentID || '',
                CollegeID: CollegeID || '',
                Term: Term || '',
                SchoolYear_SIS: SchoolYear_SIS || '',
                GradeLevel: GradeLevel || '',
                FirstName: FirstName || '',
                LastName: LastName || '',
            })
            .pipe(
                map((res) => {
                    const body = <StudentSearchResult[]>(res || []);

                    body.forEach((item) => {
                        item.BirthDate = item.BirthDate ? new Date(item.BirthDate) : null;
                    });

                    return body;
                })
            );
    }

    // #endregion

    // #region Search For User Service Call

    SearchForUser(AuthToken: string, FirstName?: string, LastName?: string) {
        return this.http.post<UserSearchResult[]>(this.configService.hostAddress + `Api/Services/DELTA/SearchForUser`, {
            AuthToken: AuthToken || '',
            FirstName: FirstName || '',
            LastName: LastName || '',
        });
    }

    // #endregion

    // #region Set Current School Service Call

    SetCurrentSchool(school: string) {
        this.CurrentSchool = school;
    }

    // #endregion

    // #region Set Selected Student Service Call

    SetSelectedStudent(AuthToken: string, student: BasicStudentInfo) {
        const self = this;

        if (!student || !AuthToken) {
            return;
        }

        if (!self.SelectedStudent || student.StudentID !== self.SelectedStudent.StudentID) {
            self.SelectedStudent = student;
        }
    }

    // #endregion

    // #region Submit Issue Report Service Call

    SubmitIssueReport(form: IssueReportInfo) {
        return this.http.post<number>(`${this.configService.hostAddress}Api/Services/DELTA/SubmitIssueReport`, {
            AuthToken: form.AuthToken || '',
            CurrentRole: form.CurrentRole || '',
            IssueID: form.IssueID || '',
            IssueDescription: form.IssueDescription || '',
            ReporterEmail: form.ReporterEmail || '',
        });
    }

    // #endregion

    // #region Test Email Service Call

    TestEmail(authToken: string, template: string, email: string, message: string | null = null) {
        return this.http.post<boolean>(`${this.configService.hostAddress}Api/Services/AdminSettings/TestEmail`, {
            AuthToken: authToken || '',
            Template: template || '',
            Email: email || '',
            Message: message || 'TEST: No Message Sent',
        });
    }

    // #endregion

    // #region Undo Remove Student College Record Course Service Call

    UndoRemoveStudentCollegeRecordCourse(AuthToken: string, DualEnrollStudentID: number, CourseChoiceID: number, AddedBy: number) {
        return this.http
            .get<CourseChoice[]>(
                this.configService.hostAddress +
                    `Api/Services/DELTA/UndoRemoveStudentCollegeRecordCourse?AuthToken=${AuthToken || ''}&DualEnrollStudentID=${
                        DualEnrollStudentID || ''
                    }&CourseChoiceID=${CourseChoiceID || ''}&AddedBy=${AddedBy || ''}`
            )
            .pipe(
                map((res) => {
                    if (!res) {
                        return res;
                    }

                    const body = <CourseChoice[]>res.slice();

                    body.forEach((data) => {
                        // Create the full coded name for each course choice returned
                        data.FullCode = data.Prefix + ' ' + data.Code;

                        data.DateCreated = data.DateCreated ? new Date(data.DateCreated) : null;
                        data.DateRemoved = data.DateRemoved ? new Date(data.DateRemoved) : null;
                        data.DateScheduled = data.DateScheduled ? new Date(data.DateScheduled) : null;
                    });

                    return body;
                })
            );
    }

    // #endregion

    // #region Update College Contact Info Service Call

    UpdateCollegeContact(AuthToken: string, CollegeContactID: number, CollegeID: number, Email: string) {
        return this.http.get<CollegeContactInfo[]>(
            this.configService.hostAddress +
                `Api/Services/DELTA/UpdateCollegeContact?AuthToken=${AuthToken || ''}
                &CollegeContactID=${CollegeContactID != null ? CollegeContactID : ''}&CollegeID=${CollegeID != null ? CollegeID : ''}&Email=${Email || ''}`
        );
    }

    // #endregion

    // #region Update College Staff Access Level Service Call

    UpdateCollegeStaffAccessLevel(AuthToken: string, CurrentRole: string, StaffID: number, AccessLevel: number) {
        return this.http.get<CollegeStaff[]>(
            this.configService.hostAddress +
                `Api/Services/DELTA/UpdateCollegeStaffAccessLevel?AuthToken=${AuthToken || ''}&CurrentRole=${
                    CurrentRole || ''
                }&StaffID=${StaffID != null ? StaffID : ''}&AccessLevel=${AccessLevel != null ? AccessLevel : ''}`
        );
    }

    // #endregion

    // #region Update College Staff Access Level Service Call
    
    UpdateStudentCompletedOrientation(AuthToken: string, DualEnrollStudentID: number, StudentID: string, CollegeID: number, CompletedOrientation: boolean)  {
        return this.http
            .get<StudentCollegeInfo>(
                this.configService.hostAddress +
                    `Api/Services/DELTA/UpdateStudentCompletedOrientation?AuthToken=${AuthToken || ''}&DualEnrollStudentID=${DualEnrollStudentID || ''}&StudentID=${StudentID || ''}&CollegeID=${
                        CollegeID || ''
                    }&CompletedOrientation=${CompletedOrientation != null ? CompletedOrientation : ''}`
            )
            .pipe(
                map((data) => {
                    data.OriginalCounselorSAPID = data.CounselorSAPID;
                    data.DualEnrollmentProgram = this.GetDualEnrollmentProgramInfo(data.DualEnrollmentProgramID);
                    data.OriginalDualEnrollmentProgramID = data.DualEnrollmentProgramID;
                    data.OriginalAcceleratedDegreeProgramID = data.AcceleratedDegreeProgramID;
                    data.AvailableTerms = data.AvailableTerms || [];
                    data.AnyOpenTerms =
                        data.AvailableTerms.findIndex((item) => {
                            return item.CurrentlyAvailable === true;
                        }) >= 0;

                    // Process all date objects into dates
                    data.AgreementTextDate = data.AgreementTextDate ? new Date(data.AgreementTextDate) : null;
                    data.CollegeApprovedDate = data.CollegeApprovedDate ? new Date(data.CollegeApprovedDate) : null;
                    data.CounselorApprovedDate = data.CounselorApprovedDate ? new Date(data.CounselorApprovedDate) : null;
                    data.DateUpdated = data.DateUpdated ? new Date(data.DateUpdated) : null;
                    data.ParentApprovedDate = data.ParentApprovedDate ? new Date(data.ParentApprovedDate) : null;
                    data.SchoolApprovedDate = data.SchoolApprovedDate ? new Date(data.SchoolApprovedDate) : null;

                    if (data.Term) {
                        data.Term.DateBegin = data.Term.DateBegin ? new Date(data.Term.DateBegin) : null;
                        data.Term.DateEnd = data.Term.DateEnd ? new Date(data.Term.DateEnd) : null;
                    }

                    if (data.AvailableTerms && data.AvailableTerms.length) {
                        data.AvailableTerms.forEach((d) => {
                            d.DateBegin = d.DateBegin ? new Date(d.DateBegin) : null;
                            d.DateEnd = d.DateEnd ? new Date(d.DateEnd) : null;
                        });
                    }

                    return data;
                })
            );
    }

    // #endregion

    // #region Update Parent Contact Info Service Call

    UpdateParentContactInfo(AuthToken: string, UserID: number, FocusStaffID: number, Phone: string, Email: string) {
        return this.http.get<ParentInfo>(
            this.configService.hostAddress +
                `Api/Services/DELTA/UpdateParentContactInfo?AuthToken=${AuthToken || ''}&UserID=${UserID || ''}&FocusStaffID=${
                    FocusStaffID || ''
                }&Phone=${Phone || ''}&Email=${Email || ''}`
        );
    }

    // #endregion

    // #region Update Student College Student ID Service Call

    UpdateStudentCollegeStudentID(AuthToken: string, CollegeID: number, StudentID: string, CollegeStudentID: string) {
        return this.http.get<string>(
            this.configService.hostAddress +
                `Api/Services/DELTA/UpdateStudentCollegeStudentID?AuthToken=${AuthToken || ''}&CollegeID=${
                    CollegeID || ''
                }&StudentID=${StudentID || ''}&StudentIdAtCollege=${CollegeStudentID || ''}`
        );
    }

    // #endregion

    // #region Update Student College Record Service Call

    UpdateStudentCollegeRecord(
        AuthToken: string,
        StudentID: string,
        DualEnrollStudentID: number,
        CounselorSAPID: string,
        FormStatus: number
    ) {
        const self = this;

        return this.http
            .get<StudentCollegeInfo[]>(
                self.configService.hostAddress +
                    `Api/Services/DELTA/UpdateStudentCollegeRecord?AuthToken=${AuthToken || ''}&StudentID=${
                        StudentID || ''
                    }&DualEnrollStudentID=${DualEnrollStudentID || ''}&CounselorSAPID=${CounselorSAPID || ''}&FormStatus=${
                        FormStatus || ''
                    }`
            )
            .pipe(
                map((res) => {
                    if (!res) {
                        return res;
                    }

                    const resdata = <StudentCollegeInfo[]>JSON.parse(JSON.stringify(res));

                    resdata.forEach((data) => {
                        // Establish the original counselor SAPID before handing back to client
                        data.OriginalCounselorSAPID = data.CounselorSAPID;
                        data.DualEnrollmentProgram = self.GetDualEnrollmentProgramInfo(data.DualEnrollmentProgramID);
                        data.OriginalDualEnrollmentProgramID = data.DualEnrollmentProgramID;
                        data.OriginalAcceleratedDegreeProgramID = data.AcceleratedDegreeProgramID;
                        data.AvailableTerms = data.AvailableTerms || [];
                        data.AnyOpenTerms =
                            data.AvailableTerms.findIndex((item) => {
                                return item.CurrentlyAvailable === true;
                            }) >= 0;

                        // Process all date objects into dates
                        data.AgreementTextDate = data.AgreementTextDate ? new Date(data.AgreementTextDate) : null;
                        data.CollegeApprovedDate = data.CollegeApprovedDate ? new Date(data.CollegeApprovedDate) : null;
                        data.CounselorApprovedDate = data.CounselorApprovedDate ? new Date(data.CounselorApprovedDate) : null;
                        data.DateUpdated = data.DateUpdated ? new Date(data.DateUpdated) : null;
                        data.ParentApprovedDate = data.ParentApprovedDate ? new Date(data.ParentApprovedDate) : null;
                        data.SchoolApprovedDate = data.SchoolApprovedDate ? new Date(data.SchoolApprovedDate) : null;

                        if (data.Term) {
                            data.Term.DateBegin = data.Term.DateBegin ? new Date(data.Term.DateBegin) : null;
                            data.Term.DateEnd = data.Term.DateEnd ? new Date(data.Term.DateEnd) : null;
                        }

                        if (data.AvailableTerms && data.AvailableTerms.length) {
                            data.AvailableTerms.forEach((d) => {
                                d.DateBegin = d.DateBegin ? new Date(d.DateBegin) : null;
                                d.DateEnd = d.DateEnd ? new Date(d.DateEnd) : null;
                            });
                        }
                    });

                    return resdata;
                })
            );
    }

    // #endregion

    // #region Update Student College Record Course Reference Number

    UpdateStudentCollegeRecordCourseReferenceNumber(
        AuthToken: string,
        DualEnrollStudentID: number,
        CourseChoiceID: number,
        UpdatedBy: number,
        ReferenceNumber: string,
        CountyTaught: boolean
    ) {
        return this.http
            .get<CourseNotesInfo>(
                this.configService.hostAddress +
                    `Api/Services/DELTA/UpdateStudentCollegeRecordCourseReferenceNumber?AuthToken=${
                        AuthToken || ''
                    }&DualEnrollStudentID=${DualEnrollStudentID || ''}&CourseChoiceID=${CourseChoiceID || ''}&UpdatedBy=${
                        UpdatedBy || ''
                    }&ReferenceNumber=${ReferenceNumber || ''}&CountyTaught=${CountyTaught != null ? CountyTaught : ''}`
            )
            .pipe(
                map((res) => {
                    if (res) {
                        const data = res.Requests;

                        if (data && !!data.length) {
                            data.forEach((d) => {
                                // Create the full coded name for each course choice returned
                                d.FullCode = d.Prefix + ' ' + d.Code;

                                d.DateCreated = d.DateCreated ? new Date(d.DateCreated) : null;
                                d.DateRemoved = d.DateRemoved ? new Date(d.DateRemoved) : null;
                                d.DateScheduled = d.DateScheduled ? new Date(d.DateScheduled) : null;
                            });
                        }

                        if (res.Notes && res.Notes.length) {
                            res.Notes.forEach((note) => {
                                note.NoteDate = note.NoteDate ? new Date(note.NoteDate) : null;
                            });
                        }
                    }

                    return res;
                })
            );
    }

    // #endregion

    // #region Update Student College Record Course Alt Reference Number

    UpdateStudentCollegeRecordCourseAltReferenceNumber(
        AuthToken: string,
        DualEnrollStudentID: number,
        CourseChoiceID: number,
        UpdatedBy: number,
        ReferenceNumber: string
    ) {
        return this.http
            .get<CourseNotesInfo>(
                this.configService.hostAddress +
                    `Api/Services/DELTA/UpdateStudentCollegeRecordCourseAltReferenceNumber?AuthToken=${
                        AuthToken || ''
                    }&DualEnrollStudentID=${DualEnrollStudentID || ''}&CourseChoiceID=${CourseChoiceID || ''}&UpdatedBy=${
                        UpdatedBy || ''
                    }&AltReferenceNumber=${ReferenceNumber || ''}`
            )
            .pipe(
                map((res) => {
                    if (res) {
                        const data = res.Requests;

                        if (data && !!data.length) {
                            data.forEach((d) => {
                                // Create the full coded name for each course choice returned
                                d.FullCode = d.Prefix + ' ' + d.Code;

                                d.DateCreated = d.DateCreated ? new Date(d.DateCreated) : null;
                                d.DateRemoved = d.DateRemoved ? new Date(d.DateRemoved) : null;
                                d.DateScheduled = d.DateScheduled ? new Date(d.DateScheduled) : null;
                            });
                        }

                        if (res.Notes && res.Notes.length) {
                            res.Notes.forEach((note) => {
                                note.NoteDate = note.NoteDate ? new Date(note.NoteDate) : null;
                            });
                        }
                    }

                    return res;
                })
            );
    }

    // #endregion

    // #region Update Student Contact Info Service Call

    UpdateStudentContactInfo(AuthToken: string, StudentID: string, Phone: string, Email: string) {
        return this.http.get<StudentContactInfo>(
            this.configService.hostAddress +
                `Api/Services/DELTA/UpdateStudentContactInfo?AuthToken=${AuthToken || ''}&StudentID=${StudentID || ''}&Phone=${
                    Phone || ''
                }&Email=${Email || ''}`
        );
    }

    // #endregion

    // #region Update Student Eligibility Service Call

    UpdateStudentEligibility(AuthToken: string, DualEnrollStudentID: number, Eligibility: number | null) {
        return this.http
            .get<StudentCollegeInfo>(
                this.configService.hostAddress +
                    `Api/Services/DELTA/UpdateStudentEligibility?AuthToken=${AuthToken || ''}&DualEnrollStudentID=${
                        DualEnrollStudentID || ''
                    }&Eligibility=${Eligibility != null ? Eligibility : ''}`
            )
            .pipe(
                map((data) => {
                    data.OriginalCounselorSAPID = data.CounselorSAPID;
                    data.DualEnrollmentProgram = this.GetDualEnrollmentProgramInfo(data.DualEnrollmentProgramID);
                    data.OriginalDualEnrollmentProgramID = data.DualEnrollmentProgramID;
                    data.OriginalAcceleratedDegreeProgramID = data.AcceleratedDegreeProgramID;
                    data.AvailableTerms = data.AvailableTerms || [];
                    data.AnyOpenTerms =
                        data.AvailableTerms.findIndex((item) => {
                            return item.CurrentlyAvailable === true;
                        }) >= 0;

                    // Process all date objects into dates
                    data.AgreementTextDate = data.AgreementTextDate ? new Date(data.AgreementTextDate) : null;
                    data.CollegeApprovedDate = data.CollegeApprovedDate ? new Date(data.CollegeApprovedDate) : null;
                    data.CounselorApprovedDate = data.CounselorApprovedDate ? new Date(data.CounselorApprovedDate) : null;
                    data.DateUpdated = data.DateUpdated ? new Date(data.DateUpdated) : null;
                    data.ParentApprovedDate = data.ParentApprovedDate ? new Date(data.ParentApprovedDate) : null;
                    data.SchoolApprovedDate = data.SchoolApprovedDate ? new Date(data.SchoolApprovedDate) : null;

                    if (data.Term) {
                        data.Term.DateBegin = data.Term.DateBegin ? new Date(data.Term.DateBegin) : null;
                        data.Term.DateEnd = data.Term.DateEnd ? new Date(data.Term.DateEnd) : null;
                    }

                    if (data.AvailableTerms && data.AvailableTerms.length) {
                        data.AvailableTerms.forEach((d) => {
                            d.DateBegin = d.DateBegin ? new Date(d.DateBegin) : null;
                            d.DateEnd = d.DateEnd ? new Date(d.DateEnd) : null;
                        });
                    }

                    return data;
                })
            );
    }

    // #endregion

    // #region Update Student FastTrack Service Call

    UpdateStudentFastTrack(AuthToken: string, DualEnrollStudentID: number, FastTrack: boolean) {
        return this.http
            .get<StudentCollegeInfo>(
                this.configService.hostAddress +
                    `Api/Services/DELTA/UpdateStudentFastTrack?AuthToken=${AuthToken || ''}&DualEnrollStudentID=${
                        DualEnrollStudentID || ''
                    }&FastTrackAdvised=${FastTrack != null ? FastTrack : ''}`
            )
            .pipe(
                map((data) => {
                    data.OriginalCounselorSAPID = data.CounselorSAPID;
                    data.DualEnrollmentProgram = this.GetDualEnrollmentProgramInfo(data.DualEnrollmentProgramID);
                    data.OriginalDualEnrollmentProgramID = data.DualEnrollmentProgramID;
                    data.OriginalAcceleratedDegreeProgramID = data.AcceleratedDegreeProgramID;
                    data.AvailableTerms = data.AvailableTerms || [];
                    data.AnyOpenTerms =
                        data.AvailableTerms.findIndex((item) => {
                            return item.CurrentlyAvailable === true;
                        }) >= 0;

                    // Process all date objects into dates
                    data.AgreementTextDate = data.AgreementTextDate ? new Date(data.AgreementTextDate) : null;
                    data.CollegeApprovedDate = data.CollegeApprovedDate ? new Date(data.CollegeApprovedDate) : null;
                    data.CounselorApprovedDate = data.CounselorApprovedDate ? new Date(data.CounselorApprovedDate) : null;
                    data.DateUpdated = data.DateUpdated ? new Date(data.DateUpdated) : null;
                    data.ParentApprovedDate = data.ParentApprovedDate ? new Date(data.ParentApprovedDate) : null;
                    data.SchoolApprovedDate = data.SchoolApprovedDate ? new Date(data.SchoolApprovedDate) : null;

                    if (data.Term) {
                        data.Term.DateBegin = data.Term.DateBegin ? new Date(data.Term.DateBegin) : null;
                        data.Term.DateEnd = data.Term.DateEnd ? new Date(data.Term.DateEnd) : null;
                    }

                    if (data.AvailableTerms && data.AvailableTerms.length) {
                        data.AvailableTerms.forEach((d) => {
                            d.DateBegin = d.DateBegin ? new Date(d.DateBegin) : null;
                            d.DateEnd = d.DateEnd ? new Date(d.DateEnd) : null;
                        });
                    }

                    return data;
                })
            );
    }

    // #endregion

    // #region Update Student Program Service Call

    UpdateStudentProgram(
        AuthToken: string,
        DualEnrollStudentID: number,
        DualEnrollmentProgramID?: number,
        AcceleratedDegreeProgramID?: number
    ) {
        return this.http
            .get<StudentCollegeInfo>(
                this.configService.hostAddress +
                    `Api/Services/DELTA/UpdateStudentProgram?AuthToken=${AuthToken || ''}&DualEnrollStudentID=${
                        DualEnrollStudentID || ''
                    }&DualEnrollmentProgramID=${DualEnrollmentProgramID || ''}&AcceleratedDegreeProgramID=${
                        AcceleratedDegreeProgramID || ''
                    }`
            )
            .pipe(
                map((data) => {
                    data.OriginalCounselorSAPID = data.CounselorSAPID;
                    data.DualEnrollmentProgram = this.GetDualEnrollmentProgramInfo(data.DualEnrollmentProgramID);
                    data.OriginalDualEnrollmentProgramID = data.DualEnrollmentProgramID;
                    data.OriginalAcceleratedDegreeProgramID = data.AcceleratedDegreeProgramID;
                    data.AvailableTerms = data.AvailableTerms || [];
                    data.AnyOpenTerms =
                        data.AvailableTerms.findIndex((item) => {
                            return item.CurrentlyAvailable === true;
                        }) >= 0;

                    // Process all date objects into dates
                    data.AgreementTextDate = data.AgreementTextDate ? new Date(data.AgreementTextDate) : null;
                    data.CollegeApprovedDate = data.CollegeApprovedDate ? new Date(data.CollegeApprovedDate) : null;
                    data.CounselorApprovedDate = data.CounselorApprovedDate ? new Date(data.CounselorApprovedDate) : null;
                    data.DateUpdated = data.DateUpdated ? new Date(data.DateUpdated) : null;
                    data.ParentApprovedDate = data.ParentApprovedDate ? new Date(data.ParentApprovedDate) : null;
                    data.SchoolApprovedDate = data.SchoolApprovedDate ? new Date(data.SchoolApprovedDate) : null;

                    if (data.Term) {
                        data.Term.DateBegin = data.Term.DateBegin ? new Date(data.Term.DateBegin) : null;
                        data.Term.DateEnd = data.Term.DateEnd ? new Date(data.Term.DateEnd) : null;
                    }

                    if (data.AvailableTerms && data.AvailableTerms.length) {
                        data.AvailableTerms.forEach((d) => {
                            d.DateBegin = d.DateBegin ? new Date(d.DateBegin) : null;
                            d.DateEnd = d.DateEnd ? new Date(d.DateEnd) : null;
                        });
                    }

                    return data;
                })
            );
    }

    // #endregion

    // #region Update Term Window For College Service Call

    UpdateTermWindowForCollege(
        AuthToken: string,
        TermWindowID: number,
        CollegeSchoolYear: string,
        TermCode: string,
        DateBegin: Date,
        DateEnd: Date
    ) {
        return this.http
            .get<CollegeTermInfo[]>(
                this.configService.hostAddress +
                    `Api/Services/DELTA/UpdateTermWindowForCollege?AuthToken=${AuthToken || ''}&TermWindowID=${
                        TermWindowID || ''
                    }&CollegeSchoolYear=${CollegeSchoolYear || ''}&TermCode=${TermCode || ''}&DateBegin=${
                        DateBegin != null ? DateBegin.toJSON() : ''
                    }&DateEnd=${DateEnd != null ? DateEnd.toJSON() : ''}`
            )
            .pipe(
                map((res) => {
                    const data = res || [];

                    data.forEach((d) => {
                        d.DateBegin = d.DateBegin != null ? new Date(d.DateBegin) : null;
                        d.DateEnd = d.DateEnd != null ? new Date(d.DateEnd) : null;
                    });

                    return data;
                })
            );
    }

    // #endregion
    
    // #region Update Test Threshold

    UpdateTestThreshold(
        AuthToken: string,
        CurrentRole: string,
        testInfo: TestThresholdInfo,
        user: UserInfo,
    ) {
        return this.http
            .post<TestThresholdInfo>(`${this.configService.hostAddress}Api/Services/DELTA/UpsertTestThreshold`, {
                AuthToken: AuthToken || '',
                CurrentRole: CurrentRole || '',
                TestThresholdID: testInfo.TestThresholdID,
                TestDescription: testInfo.TestDescription || '',
                ContentCode: testInfo.ContentCode || '',
                Description: testInfo.Description || '',
                TestName: testInfo.TestName || '',
                ScoreType: testInfo.ScoreType || '',
                Score: testInfo.Score,
                Active: testInfo.Active,
                DateUpdated: new Date().toISOString(),
                UpdatedBy: user.UserID,
                UpdatedByLastName: user.LastName || '',
                UpdatedByFirstName: user.FirstName || '',
                UpdatedByLocalJobTitle: user.LocalJobTitle || '',
            });
    }

    // #endregion

    // #region Update Web App Issue Alert Service Call

    UpdateWebAppIssueAlert(
        AuthToken: string,
        CurrentRole: string,
        WebAppIssueAlertID: number,
        AlertMessage: string,
        DateBegin: Date,
        DateEnd: Date
    ) {
        return this.http
            .post<IssueAlertInfo[]>(`${this.configService.hostAddress}Api/Services/DELTA/UpdateWebAppIssueAlert`, {
                AuthToken: AuthToken || '',
                CurrentRole: CurrentRole || '',
                WebAppIssueAlertID: WebAppIssueAlertID || '',
                AlertMessage: AlertMessage || '',
                DateBegin: DateBegin ? DateBegin.toISOString() : '',
                DateEnd: DateEnd ? DateEnd.toISOString() : '',
            })
            .pipe(
                map((alerts) => {
                    if (alerts && !!alerts.length) {
                        alerts.forEach((alert) => {
                            alert.DateBegin = alert.DateBegin ? new Date(alert.DateBegin) : null;
                            alert.DateEnd = alert.DateEnd ? new Date(alert.DateEnd) : null;
                            alert.LastUpdated = alert.LastUpdated ? new Date(alert.LastUpdated) : null;
                        });
                    }

                    return alerts;
                })
            );
    }

    // #endregion

    // #region Update Web App Issue Sort Order Service Call

    UpdateWebAppIssueSortOrder(AuthToken: string, CurrentRole: string, IssueID: number, SortOrder: number) {
        return this.http.get<WebAppIssueInfo[]>(
            `${this.configService.hostAddress}Api/Services/DELTA/UpdateWebAppIssueSortOrder?AuthToken=${AuthToken || ''}&CurrentRole=${
                CurrentRole || ''
            }&IssueID=${IssueID || ''}&SortOrder=${SortOrder || ''}`
        );
    }

    // #endregion

    // #region Update Web App Issue Type Service Call

    UpdateWebAppIssueType(
        AuthToken: string,
        CurrentRole: string,
        IssueID: number,
        DisplayText: string,
        RequiresDescription: boolean,
        SortOrder: number
    ) {
        return this.http.post<WebAppIssueInfo[]>(`${this.configService.hostAddress}Api/Services/DELTA/UpdateWebAppIssueType`, {
            AuthToken: AuthToken || '',
            CurrentRole: CurrentRole || '',
            IssueID: IssueID || '',
            DisplayText: DisplayText || '',
            RequiresDescription: RequiresDescription || '',
            SortOrder: SortOrder || '',
        });
    }

    // #endregion
}

// #region Custom Classes

// #region Add Student College Record Return Class

export class AddStudentCollegeRecordReturn {
    Requests?: StudentCollegeInfo[];
    AddedDualEnrollStudentID?: number;
}

// #endregion

// #region Adoption Request Info Class

export class AdoptionRequestInfo {
    AdoptionRequestID?: number;
    SchoolNumber?: string;
    SchoolName?: string;
    Code?: string;
    Name?: string;
    GradRequirementID?: number;
    GradRequirementName?: string;
    HSCredit?: number;
    ProposedUseID?: number;
    ProposedUseDescription?: string;
    ModificationsNeeded?: string;
    CompanionFieldsNeeded?: string;
    CompanionFieldsReason?: string;
    SchoolYear?: string;
    RequestedBy?: number;
    RequestedByFirstName?: string;
    RequestedByLastName?: string;
    RequestedByEmail?: string;
    DateRequested?: Date;
    ReviewedBy?: number;
    ReviewedByFirstName?: string;
    ReviewedByLastName?: string;
    ReviewedByEmail?: string;
    DateReviewed?: Date;
    Approved?: boolean;
    CollegeIDs?: number[];
    Colleges?: CollegeInfo[];
}

// #endregion

// #region Basic Student Info Class

export class BasicStudentInfo {
    StudentID?: string;
    LastName?: string;
    FirstName?: string;
    GradeLevel?: string;
    SchoolNumber?: string;
    SchoolName?: string;
}

// #endregion

// #region College Agreement Class

export class CollegeAgreement {
    Text?: string;
    Version?: number;
    DateCreated?: Date;
    ConsentText?: string;
    CollegeID?: number;
    Verified?: boolean;
}

// #endregion

// #region College Agreement Approval Info Class

export class CollegeAgreementApprovalInfo {
    UserID?: number;
    FocusStaffID?: number;
    CollegeID?: number;
    AgreementTextID?: number;
    DateApproved?: Date;
    TermWindowID?: number;
    AsProxy?: boolean;
    ProxyParentName?: string;
    ProxyRelationship?: number;
    ProxyDescription?: string;
}

// #endregion

// #region College Info Class

export class CollegeContactInfo {
    CollegeContactID: number;
    CollegeID: number;
    Email: string;
    CollegeName: string;
    CollegeAbbreviation: string;
    Active: boolean;
}

// #endregion

// #region College Course Info Class

export class CollegeCourseInfo {
    DualEnrollCollegeCourseID: number;
    CollegeID?: number;
    SchoolYear?: string;
    Prefix?: string;
    Code?: string;
    FullCode?: string;
    Name?: string;
    Credits?: number;
    Prerequisites?: string;
    Corequisites?: string;
    Description?: string;
    Department?: number;
    Eligible?: boolean;
    IsModular?: boolean;
    ModularCode?: string;
    ModularSortOrder?: number;
    DualEnrollGradRequirementID?: number;
    GradReqName?: string;
    HSCredit?: number;
    PriorityRequest?: boolean;
}

// #endregion

// #region College Courses List Info Class

export class CollegeCoursesListInfo {
    CollegeID: number;
    SchoolYear: string;
    Departments: CollegeDepartmentInfo[];
    Courses: CollegeCourseInfo[];
}

// #endregion

// #region College Department Info CLass

export class CollegeDepartmentInfo {
    DepartmentID?: number;
    Department: string;
    Courses: CollegeCourseInfo[];
}

// #endregion

// #region College Info Class

export class CollegeInfo {
    CollegeID: number;
    CollegeName: string;
    CollegeAbbreviation: string;
    Active: boolean;
    AnyOpenTerms?: boolean;
    Terms: CollegeTermInfo[];
}

// #endregion

// #region College Record Note Class

export class CollegeRecordNote {
    NoteID?: number;
    DualEnrollStudentID?: number;
    NoteCreator?: number;
    LastName?: string;
    FirstName?: string;
    NoteDate?: Date;
    Note?: string;
    NoteVisible?: boolean;
}

// #endregion

// #region College Staff Class

export class CollegeStaff {
    StaffID?: number;
    CollegeID?: number;
    CollegeName?: string;
    AccessLevel?: number;
    IsAdmin?: boolean;
    IsAdvisor?: boolean;
    IsSupport?: boolean;
    UserID?: number;
    FirstName?: string;
    LastName?: string;
}

// #endregion

// #region College Term Info Class

export class CollegeTermInfo {
    TermWindowID?: number;
    CollegeSchoolYear?: string;
    TermCode?: string;
    DateBegin?: Date;
    DateEnd?: Date;
    CurrentlyAvailable?: boolean;
    InUse?: boolean;
}

// #endregion

// #region Course Choice Class

export class CourseChoice {
    CourseChoiceID?: number;
    DualEnrollStudentID?: number;
    CollegeCourseID?: number;
    Prefix?: string;
    Code?: string;
    FullCode?: string;
    PriorityRequest?: string;
    Name?: string;
    Credits?: number;
    SchoolYear?: string;
    AddedBy?: number;
    DateCreated?: Date;
    AddedByFirstName?: string;
    AddedByLastName?: string;
    RemovedBy?: number;
    DateRemoved?: Date;
    RemovedByFirstName?: string;
    RemovedByLastName?: string;
    ScheduledBy?: number;
    DateScheduled?: Date;
    ScheduledByFirstName?: string;
    ScheduledByLastName?: string;
    ReferenceNumber?: string;
    AltReferenceNumber?: string;
    CountyTaught?: boolean;
    IsModular?: boolean;
    ModularCode?: string;
    ModularSortOrder?: number;
    DualEnrollGradRequirementID?: number;
    GradReqName?: string;
    HSCredit?: number;
}

// #endregion

// #region Course Notes Info Class

export class CourseNotesInfo {
    Requests?: CourseChoice[];
    Notes?: CollegeRecordNote[];
}

// #endregion

// #region Designee Info Class

export class DesigneeInfo {
    DesigneeID?: number;
    UserID?: number;
    FirstName?: string;
    LastName?: string;
    Email?: string;
    SchoolNumber?: string;
    SchoolName?: string;
    RoleID?: number;
    RoleName?: string;
    CanBypassAdmin?: boolean;
    DateAdded?: Date;
    AddedBy?: number;
    AddedByFirstName?: string;
    AddedByLastName?: string;
}

// #endregion

// #region Designee Rights Info Class

export class DesigneeRightsInfo {
    WorkLocation?: string;
    SchoolName?: string;
    CanBypassAdmin?: boolean;
}

// #endregion

// #region Eligible Course Info Class

export class EligibleCourseInfo {
    EligibleCourseID?: number;
    Prefix?: string;
    Code?: string;
    Colleges?: CollegeInfo[];
}

// #endregion

// #region Employee Info Class

export class EmployeeInfo {
    SAPID: string;
    LastName: string;
    FirstName: string;
    LocalJobTitle: string;
    UserID?: number;
    WorkLocation: string;
    PositionNo: string;
}

// #endregion

// #region Grad Requirement Info Class

export class GradRequirementInfo {
    GradRequirementID?: number;
    Name?: string;
}

// #endregion

// #region Issue Alert Info Class

export class IssueAlertInfo {
    WebAppIssueAlertID?: number;
    SystemMasterID?: number;
    AlertMessage?: string;
    DateBegin?: Date;
    DateEnd?: Date;
    InEffect?: boolean;
    AddedBy?: number;
    AddedByLastName?: string;
    AddedByFirstName?: string;
    AddedBySAPID?: string;
    AddedByEmail?: string;
    LastUpdated?: Date;
}

// #endregion

// #region Issue Report Info Class

export class IssueReportInfo {
    AuthToken?: string;
    CurrentRole?: string;
    WebAppIssueReportID?: number;
    UserID?: number;
    FocusStaffID?: number;
    ReportedByLastName?: string;
    ReportedByFirstName?: string;
    ReportedBySAPID?: string;
    ReportedByADEmail?: string;
    ReportedByStudentID?: string;
    DateReported?: Date;
    SystemMasterID?: number;
    IssueID?: number;
    IssueName?: string;
    IssueDescription?: string;
    ReporterEmail?: string;
    ReporterRole?: number;
    ReporterRoleName?: string;
    Resolved?: boolean;
    ResolvedBy?: number;
    ResolvedByLastName?: string;
    ResolvedByFirstName?: string;
    ResolvedBySAPID?: string;
    ResolvedDate?: Date;
    ResolvedComments?: string;
}

// #endregion

// #region New Account Info Class

export class NewAccountInfo {
    Username?: string;
    UserID?: number;
    Password?: string;
    Email?: string;
    NewAccount?: boolean;
    FirstName?: string;
    LastName?: string;
    CollegeName?: string;
    RoleName?: string;
    Staff?: CollegeStaff[];
    Designees?: DesigneeInfo[];
    SchoolName?: string;
    SchoolNumber?: string;
    CanBypassAdmin?: boolean;
}

// #endregion

// #region Parent Info Class

export class ParentInfo {
    ParentContactID?: number;
    Email?: string;
    FocusStaffID?: number;
    Phone?: string;
    UserID?: number;
}

// #endregion

// #region Program Info Class

export class ProgramInfo {
    ProgramID?: number;
    AcceleratedDegreeProgramID?: number;
    Name?: string;
    MinimumCredits?: number;
    MaximumCredits?: number;
    CreditsText?: string;
}

// #endregion

// #region Role Info Class

export class RoleInfo {
    RoleID?: number;
    RoleName?: string;
    CanBypass?: boolean;
}

// #endregion

// #region School Counselor Info Class

export class SchoolCounselorInfo {
    EmployeeFirstName: string;
    EmployeeLastName: string;
    EmployeeSAPID: string;
    SchoolNumber: string;
}

// #endregion

// #region School Info Class

export class SchoolInfo {
    SchoolYear?: string;
    SchoolNumber?: string;
    SchoolName?: string;
    SchoolPhoneNumber?: string;
    Principal?: string;
    Magnet?: boolean;
    Charter?: boolean;
    Area?: string;
    AreaDescription?: string;
    Enr_PK?: boolean;
    Enr_KG?: boolean;
    Enr_01?: boolean;
    Enr_02?: boolean;
    Enr_03?: boolean;
    Enr_04?: boolean;
    Enr_05?: boolean;
    Enr_06?: boolean;
    Enr_07?: boolean;
    Enr_08?: boolean;
    Enr_09?: boolean;
    Enr_10?: boolean;
    Enr_11?: boolean;
    Enr_12?: boolean;
    Enr_30?: boolean;
    Enr_31?: boolean;
    Uses_Elegrade?: boolean;
    ExcludeFromEnrCounts?: boolean;
    IsElementary?: boolean;
    IsMiddle?: boolean;
    IsSenior?: boolean;
    IsAdult?: boolean;
    DOE_School_Number?: string;
    CommunityEligibilityOption?: boolean;
    FiscalYear?: string;
    BoardDistrict?: string;
}

// #endregion

// #region Student Agreements Status Class

export class StudentAgreementsStatus {
    PCSBAgreed: boolean;
    CollegeAgreed: boolean;
}

// #endregion

// #region Student College Info Class

export class StudentCollegeInfo {
    DualEnrollStudentID?: number;
    StudentID?: string;
    SchoolYear?: string;
    Term?: CollegeTermInfo;
    DateUpdated?: Date;
    CollegeID?: number;
    CollegeName?: string;
    CollegeSchoolYear?: string;
    FormStatus?: number;
    DualEnrollmentProgramID?: number;
    DualEnrollmentProgram?: ProgramInfo;
    AcceleratedDegreeProgramID?: number;
    OriginalDualEnrollmentProgramID?: number;
    OriginalAcceleratedDegreeProgramID?: number;
    CounselorSAPID?: string;
    OriginalCounselorSAPID?: string;
    CounselorApproved?: boolean;
    CounselorApprovedUser?: number;
    CounselorApprovedDate?: Date;
    SchoolApproved?: boolean;
    SchoolApprovedUser?: number;
    SchoolApprovedDate?: Date;
    CollegeApproved?: boolean;
    CollegeApprovedUser?: number;
    CollegeApprovedDate?: Date;
    ParentHasApproved?: boolean;
    ParentApprovedUser?: number;
    ParentApprovedDate?: Date;
    AsProxy?: boolean;
    ProxyParentName?: string;
    ProxyRelationship?: number;
    ProxyDescription?: string;
    StudentCollegeAgreementApproved?: boolean;
    StudentPolkAgreementApproved?: boolean;
    StudentCollegeID?: string;
    AgreementText?: string;
    AgreementTextDate?: Date;
    AgreementConsentText?: string;
    AvailableTerms?: CollegeTermInfo[];
    AnyOpenTerms?: boolean;
    HistoricalRecord?: boolean;
    EligibilityByCourseGrade?: number;
    FastTrackAdvised?: boolean;
    CompletedOrientation?: boolean;
    SchoolNumber?: string;
}

// #endregion

// #region Student Contact Info Class

export class StudentContactInfo {
    StudentID: string;
    Phone: string;
    EmailAddress: string;
}

// #endregion

// #region Student Info Class

export class StudentInfo extends BasicStudentInfo {
    SchoolYear?: string;
    IsPastDemo?: boolean;
    SSN?: string;
    HomePhone?: string;
    BirthDate?: Date;
    Address?: string;
    Apartment?: string;
    City?: string;
    State?: string;
    Zip?: string;
    MailingAddress?: string;
    MailingApartment?: string;
    MailingCity?: string;
    MailingState?: string;
    MailingZip?: string;
    UnweightedGPA?: number;
    UnweightedCollegeGPA?: number;
    ParentUserID?: number;
    ParentFirstName?: string;
    ParentLastName?: string;
    ParentEmail?: string;
    ParentPhone?: string;
    TestHistory?: StudentTestInfo[];
    StudentPhone?: string;
    StudentEmail?: string;
}

// #endregion

// #region Student Search Result Class

export class StudentSearchResult {
    StudentID?: string;
    FirstName?: string;
    MiddleName?: string;
    LastName?: string;
    SSN?: string;
    HomePhone?: string;
    BirthDate?: Date;
    MailingAddress?: string;
    MailingApartment?: string;
    MailingCity?: string;
    MailingState?: string;
    MailingZip?: string;
    SchoolNumber?: string;
    SchoolName?: string;
    GradeLevel?: string;
    SchoolYear?: string;
    Term?: string;
    Credits?: string;
    GPA?: string;
    // CollegeGPA?: string;
    SATVerbal?: string;
    SATMath?: string;
    CPTReading?: string;
    CPTSentence?: string;
    CPTMath?: string;
    ACTVerbal?: string;
    ACTMath?: string;
    SchoolApproved?: boolean;
    CollegeApproved?: boolean;
    CounselorApproved?: boolean;
    ParentApproved?: boolean;
    DualEnrollStudentID?: number;
    HistoricalRecord?: boolean;
}

// #endregion

// #region Student Test Info Class

export interface StudentTestInfo {
    TestDescription: string;
    Years: StudentTestInfoYear[];
}

// #endregion

// #region Student Test Info Score

export interface StudentTestInfoScore {
    Outdated: boolean;
    PastThreshold: boolean;
    Score: number;
    TestDate: Date;
}

// #endregion

// #region Student Test Info Subject

export interface StudentTestInfoSubject {
    Description: string;
    Exams: StudentTestInfoScore[];
}

// #endregion

// #region Student Test Info Year

export interface StudentTestInfoYear {
    GradeLevel: string;
    SchoolYear: string;
    Subjects: StudentTestInfoSubject[];
}

// #endregion

// #region Test Threshold Info

export interface TestThresholdInfo {
    TestThresholdID: number | null;
    TestDescription: string;
    ContentCode: string;
    Description: string;
    TestName: string;
    ScoreType: string;
    Score: number | null;
    Active: boolean;
    DateUpdated: Date | null;
    UpdatedBy: number | null;
    UpdatedByLastName: string;
    UpdatedByFirstName: string;
    UpdatedByLocalJobTitle: string;
}

// #endregion

// #region User Search Result Class

export class UserSearchResult {
    FirstName?: string;
    LastName?: string;
    City?: string;
    Email?: string;
    UserID?: number;
    Username?: string;
    AsAdmin?: boolean;
    SAPID?: string;
    PositionNo?: string;
    LocalJobTitle?: string;
}

// #endregion

// #region Web App Issue Info Class

export class WebAppIssueInfo {
    SystemMasterID?: number;
    IssueID?: number;
    DisplayText?: string;
    RequiresDescription?: boolean;
    SortOrder?: number;
    InUse?: boolean;
}

// #endregion

// #endregion

// #region Custom Enumerations

// #region College Staff Access Level

export enum CollegeStaffAccessLevel {
    SUPPORT_STAFF = 0,
    ADVISOR = 1,
    ADMINISTRATOR = 2,
}

// #endregion

// #endregion



// WEBPACK FOOTER //
// ./src/services/application/application.service.ts